// MemberProfile — canonical account-screen component, consumed by
// workspace-shell's /my + /profile + /settings pages AND canvas's
// /settings (shared/pages/profile.js). Reads from findMe(s) which
// resolves to the viewed person (via ?u= query or current user).
// Sections: Hero, Details, Equity, Documents, Checklist.
//
// Config via parent element props:
//   editable  — whether fields are editable (boolean fn or static)
//   showPrivate — whether equity/docs/checklist are visible
//
// Every prop factory below calls `findMe(s)` directly — a former
// `const _me = findMe` alias used to sit between them, and while it
// resolved fine for THIS file's own render pass, frank's serializer could
// not chase the alias back to the real `findMe` export when re-extracting
// any of these nested prop factories as a standalone unit (dozens of
// `unresolvable-identifier` findings on `_me`, all latent ReferenceErrors,
// none of them tripped in the app only because nothing here had exercised
// the affected re-extraction path yet). Removed the alias, called `findMe`
// everywhere `_me` used to be. See tickets/fable.md "decompose
// workspace-shell's 507 defects by owning package".
//
// Moved from workspace/components/MemberProfile.js into `shared`
// (2026-08-15) per Nika's ruling on tickets/opus.md ("extract MemberProfile
// into packages/shared … so canvas and the shell both consume ONE
// implementation" — NOT the twelve-fix shared/** grant from the same day).
// findMe/canAdmin/canSuperadmin/canEditProfile/canAccessPrivate/
// isSuperAdmin/getSDK/patchPeopleList/signoutAndRedirect/teams.* all now
// resolve from sibling files in THIS package instead of workspace's —
// see peopleUtils.js, memberProfileAccess.js, sdk.js, signout.js, teams.js.
import { findMe, patchPeopleList } from '../functions/peopleUtils.js'
// canAdmin/canSuperadmin/canEditProfile/canAccessPrivate/isSuperAdmin are
// resolved via el.call (not a static import) throughout this file — see
// the `canAdmin` el.call note below at the ProfileAvailability AvailHint
// factory for why: it lets workspace-shell keep resolving to its own
// (fuller) roleHelpers.js implementation while canvas resolves to this
// package's memberProfileAccess.js, the only version it has.
import { getSDK } from '../functions/sdk.js'
import { signoutAndRedirect } from '../functions/signout.js'
// Named imports, not `import * as teams`: frank cannot serialize a
// namespace binding, so the emitted _writeUserPatch read a `teams` that
// resolved to nothing (frank 3.14.793 refuses the shared emission for it).
import {
  addMemberToTeam,
  createTeam,
  personTeams,
  primaryTeamName,
  removeMemberFromTeam,
  teamsInOrg
} from '../functions/teams.js'
// Was workspace/functions/featureFlags.js USE_PROFILES_OVERLAY — a static
// `true` (not a runtime toggle), inlined here rather than dragging the rest
// of that flag catalog (workspace-only, unrelated to MemberProfile) into
// this package. Keep in sync with the workspace copy if it ever flips.
const USE_PROFILES_OVERLAY = true

// Map user_profiles column name → profiles column name. Columns absent
// from the map are user_profiles-only (financial) and are not mirrored.
// `department` and `group_name` used to live here; removed with §I10 —
// team membership is now Mongo-backed via `functions/teams.js`.
const _HR_TO_PROFILES = {
  name: 'display_name',
  position: 'title',
  github: 'github',
  location: 'location',
  timezone: 'timezone',
  weekly_hours: 'weekly_target_hours',
  working_hours_start: 'working_hours_start',
  working_hours_end: 'working_hours_end'
}

// Translate a user_profiles patch to a profiles patch. Returns null if
// no HR fields touched (e.g., pure financial edits) — skip the mirror.
const _toProfilesPatch = (patch) => {
  const out = {}
  for (const [k, v] of Object.entries(patch)) {
    const mapped = _HR_TO_PROFILES[k]
    if (!mapped) continue
    // profiles.weekly_target_hours is numeric; coerce.
    if (mapped === 'weekly_target_hours') {
      const n = Number(String(v ?? '').replace(/[^0-9.]/g, ''))
      out[mapped] = Number.isFinite(n) ? n : null
    } else {
      out[mapped] = v
    }
  }
  return Object.keys(out).length ? out : null
}
const _numberFmt = new Intl.NumberFormat('en-US')
const _fmt = (n) => (n == null ? '—' : _numberFmt.format(n))
const _timezoneOptions = () => {
  try {
    const list =
      typeof Intl !== 'undefined' && Intl.supportedValuesOf
        ? Intl.supportedValuesOf('timeZone')
        : null
    if (list && list.length) return list
  } catch {}
  return [
    'UTC',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Moscow',
    'Asia/Tbilisi',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Shanghai',
    'Asia/Tokyo',
    'Australia/Sydney',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Sao_Paulo'
  ]
}

const _countryOptions = [
  'Georgia',
  'United States',
  'United Kingdom',
  'Germany',
  'France',
  'Spain',
  'Netherlands',
  'Sweden',
  'Norway',
  'Denmark',
  'Poland',
  'Ukraine',
  'Turkey',
  'UAE',
  'India',
  'China',
  'Japan',
  'Australia',
  'Brazil',
  'Canada',
  'Mexico',
  'Argentina',
  'Ireland',
  'Portugal',
  'Italy',
  'Greece',
  'Czech Republic',
  'Hungary',
  'Romania',
  'Bulgaria',
  'Estonia',
  'Latvia',
  'Lithuania'
].sort((a, b) => a.localeCompare(b))

// Legacy `location` field is a "City, CC" string. Extract country for the
// Country select; everything before the last comma becomes City.
const _countryCodes = {
  GE: 'Georgia',
  US: 'United States',
  UK: 'United Kingdom',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  PL: 'Poland',
  UA: 'Ukraine',
  TR: 'Turkey',
  AE: 'UAE',
  IN: 'India',
  CN: 'China',
  JP: 'Japan',
  AU: 'Australia',
  BR: 'Brazil',
  CA: 'Canada',
  MX: 'Mexico',
  AR: 'Argentina',
  IE: 'Ireland',
  PT: 'Portugal',
  IT: 'Italy',
  GR: 'Greece',
  CZ: 'Czech Republic',
  HU: 'Hungary',
  RO: 'Romania',
  BG: 'Bulgaria',
  EE: 'Estonia',
  LV: 'Latvia',
  LT: 'Lithuania'
}

const _countryOf = (person) => {
  if (person?.country) return person.country
  const loc = person?.location || ''
  const parts = loc
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const tail = parts[parts.length - 1] || ''
  if (_countryCodes[tail.toUpperCase()]) return _countryCodes[tail.toUpperCase()]
  if (_countryOptions.includes(tail)) return tail
  return ''
}

const _cityOf = (person) => {
  if (person?.city) return person.city
  const loc = person?.location || ''
  const parts = loc
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length >= 2) return parts.slice(0, -1).join(', ')
  return ''
}

const _tenure = (start) => {
  if (!start) return '—'
  const ms = Date.now() - new Date(start).getTime()
  const totalMonths = Math.floor(ms / (1000 * 60 * 60 * 24 * 30.44))
  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12
  if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`
  if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`
  return `${years} year${years !== 1 ? 's' : ''} and ${months} month${months !== 1 ? 's' : ''}`
}

// Debounced Supabase write — per-field timers prevent cross-field cancellation
const _patchTimers = {}
// Pending patches indexed by field — populated by _patchUser, consumed by _flushPendingPatches.
const _pendingUserPatches = {}

const IDENTITY_FIELDS = new Set(['name', 'avatar', 'timezone', 'email'])

/**
 * Splits outgoing patches into Identity and HR segments,
 * and routes them to their respective API endpoints based
 * on whether the user is editing their own profile or
 * acting as an admin editing someone else's.
 */
const _writeUserPatch = async (person, patch, isSelf, root) => {
  const sdk = getSDK()
  const identityPatch = {}
  const hrPatch = {}
  let teamPatch = undefined

  for (const [k, v] of Object.entries(patch)) {
    if (k === 'team') {
      teamPatch = v
    } else if (IDENTITY_FIELDS.has(k)) {
      identityPatch[k] = v
    } else {
      hrPatch[k] = v
    }
  }

  if (teamPatch !== undefined && root) {
    const orgId = root?.jwtClaims?.workspace_org_id || (root?.jwtClaims?.orgs || [])[0]?.id
    if (orgId) {
      const current = personTeams(person, root).filter((t) => !t.isSystem)[0]
      if (current && current.orgId === orgId) {
        await removeMemberFromTeam(root, {
          orgId,
          teamId: current.id,
          userId: person.userId,
          email: person.email
        })
      }
      if (teamPatch) {
        const target = teamsInOrg(orgId, root).find((t) => t.name === teamPatch)
        if (target) {
          await addMemberToTeam(root, {
            orgId,
            teamId: target.id,
            userId: person.userId,
            email: person.email
          })
        }
      }
    }
  }

  // Identity Routing
  if (Object.keys(identityPatch).length > 0) {
    if (isSelf) {
      await sdk.updateUserProfile(identityPatch)
    } else {
      await sdk.updateUser(person.userId, identityPatch)
    }
  }

  // HR Routing
  if (Object.keys(hrPatch).length > 0) {
    await sdk.updateUserHrProfile(person.userId, hrPatch)
  }

  // Write to public.profiles when overlay flag is on
  // Update once DB migration to MongoDB is complete
  // legacy Supabase DB is no longer being used
  if (USE_PROFILES_OVERLAY && person.email) {
    const mapped = _toProfilesPatch(patch)
    if (mapped) {
      try {
        await sdk.updateUserHrProfile(person.userId, {
          email: person.email,
          ...mapped,
          updated_at: new Date().toISOString()
        })
      } catch (error) {
        console.warn('[MemberProfile] profiles mirror failed', error?.message)
      }
    }
  }
}

const _patchUser = (el, s, patch) => {
  const person = findMe(s)
  if (!person?.userId) return

  // Check if user is editing their profile
  const isSelf = !!(
    (person.email && s.root?.currentUserEmail === person.email) ||
    (person.userId && s.root?.currentUserId === person.userId)
  )

  // Sync both directories so stale values don't resurface after navigate. Closes #2218.
  s.root.update({
    people: patchPeopleList(s.root.people, person.userId, patch),
    peopleAll: patchPeopleList(s.root.peopleAll, person.userId, patch)
  })
  const field = Object.keys(patch)[0]
  const prevValue = person[field]
  // Stage the patch so _flushPendingPatches can flush it synchronously.
  _pendingUserPatches[field] = { person, patch, isSelf, root: s.root, prevValue }
  clearTimeout(_patchTimers[field])
  if (!s.root?._profileEditing) {
    _patchTimers[field] = setTimeout(async () => {
      delete _pendingUserPatches[field]
      try {
        await _writeUserPatch(person, patch, isSelf, s.root)
      } catch (e) {
        console.warn('patch failed', e)
        const msg = e.message || String(e)
        if (msg.includes('permission') && el) {
          el.call('openNotification', {
            title: el.call('polyglot', 'account.member.permissionDenied.title'),
            message: el.call('polyglot', 'account.member.permissionDenied.message'),
            type: 'error'
          })
        }

        const revertPatch = { [field]: prevValue !== undefined ? prevValue : undefined }
        s.root.update({
          people: patchPeopleList(s.root.people, person.userId, revertPatch),
          peopleAll: patchPeopleList(s.root.peopleAll, person.userId, revertPatch)
        })
      }
    }, 400)
  }
}

// Flush all pending debounced patches immediately (cancel timers, write now).
const _flushPendingPatches = async (el) => {
  const entries = Object.entries(_pendingUserPatches)
  if (!entries.length) return
  // Cancel all pending debounce timers.
  for (const field of Object.keys(_pendingUserPatches)) {
    clearTimeout(_patchTimers[field])
    delete _patchTimers[field]
  }
  // Fire all writes concurrently. Each entry is { person, patch, isSelf, root }.
  await Promise.all(
    entries.map(async ([field, { person, patch, isSelf, root, prevValue }]) => {
      delete _pendingUserPatches[field]
      try {
        await _writeUserPatch(person, patch, isSelf, root)
      } catch (e) {
        console.warn('[flushPendingPatches] patch failed', e)
        const msg = e.message || String(e)
        if (msg.includes('permission') && el) {
          el.call('openNotification', {
            title: el.call('polyglot', 'account.member.permissionDenied.title'),
            message: el.call('polyglot', 'account.member.permissionDenied.message'),
            type: 'error'
          })
        }

        const revertPatch = { [field]: prevValue !== undefined ? prevValue : undefined }
        root.update({
          people: patchPeopleList(root.people, person.userId, revertPatch),
          peopleAll: patchPeopleList(root.peopleAll, person.userId, revertPatch)
        })
      }
    })
  )
}

// The read side of a row. A value factory answers the text or an empty
// marker ('' / '—'); an empty value reads "Not set" in the placeholder ink,
// never a bare dash. `valueFn` rides `scope` (a factory parameter read from a
// nested closure does not survive frank serialization — see EditView below).
//
// A row may hand a FIXED text instead (a `{{ }}` template: Security's "Coming
// soon"). It has no value to read and renders as given. Calling it as a
// function emptied both Security rows and threw in the console (live,
// 2026-10-29; memberProfileReadValue.test.js).
const _isEmptyValue = (v) => v == null || v === '' || v === '—' || v === '-'
const _VALUE_TEXT = {
  tag: 'span',
  fontSize: 'A',
  fontWeight: '500',
  textAlign: 'right',
  overflowWrap: 'anywhere',
  minWidth: '0'
}
const _readValue = (valueFn) =>
  typeof valueFn !== 'function'
    ? { ..._VALUE_TEXT, color: 'title', text: valueFn }
    : {
        ..._VALUE_TEXT,
        scope: { valueFn },
        color: (el, s) => (_isEmptyValue(el.scope.valueFn(el, s)) ? 'placeholder' : 'title'),
        text: (el, s) => {
          const v = el.scope.valueFn(el, s)
          return _isEmptyValue(v) ? el.call('polyglot', 'account.member.notSet') : v
        }
      }

// Row builder — label/value with optional inline editing.
//
// A definition row: the caption label and its value share a line and the
// line WRAPS when the value is long (a Georgian label, an address), so the
// value drops under its label instead of squeezing it. No hover wash: a
// read row is not a control. Rows sit apart by their own block inset
// (`Z`, was `B`: 66 px per row measured live, 2026-10-28), no stroke.
const _row = (label, valueFn, opts = {}) => ({
  flow: 'x',
  flexWrap: 'wrap',
  align: 'baseline space-between',
  gap: 'X B',
  padding: 'Z 0',
  minWidth: '0',
  hide: opts.hide || null,
  // A row with a hint is a SETTINGS row (Security's Two-factor): its label
  // column takes the room and the value stays on the right, the contract of
  // the password row's label. With no basis the long hint pushed "Coming
  // soon" under itself, to the left (live, 2026-10-29).
  RowLabel: opts.hint
    ? {
        flow: 'y',
        gap: 'Y',
        flex: '1 1 var(--spacing-E)',
        minWidth: '0',
        LabelText: { tag: 'span', fontSize: 'A', fontWeight: '700', color: 'title', text: label },
        Hint: { tag: 'span', fontSize: 'Z2', color: 'caption', text: opts.hint }
      }
    : { tag: 'span', fontSize: 'Z2', color: 'caption', text: label },
  // Editable rows render two views side-by-side: a static span shown when
  // not in edit mode, and an input/select shown when editing.
  RowValue: opts.editable
    ? {
        flow: 'x',
        align: 'center right',
        minWidth: '0',
        ReadView: {
          ..._readValue(valueFn),
          hide: (el, s) => !!s.root?._profileEditing && el.call('canEditProfile', findMe(s))
        },
        EditView: opts.combobox
          ? {
              extends: 'AppCombobox',
              scope: {
                valueFn,
                opts,
                pick(box, value) {
                  const rowOpts = box.scope.opts
                  if (rowOpts.field) _patchUser(box, box.state, { [rowOpts.field]: value || null })
                  box.state.update({ open: false, query: '' })
                }
              },
              hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', findMe(s)),
              width: 'F',
              placeholder: opts.placeholder || '',
              options: (el, s) => {
                const list =
                  typeof el.scope.opts.options === 'function'
                    ? el.scope.opts.options()
                    : el.scope.opts.options
                return [
                  { value: '', label: el.call('polyglot', 'account.member.select.none') },
                  ...list.map((v) => ({ value: v, label: v }))
                ]
              },
              current: (el, s) => {
                const v = el.scope.valueFn(el, s)
                return v === '—' ? '' : v || ''
              }
            }
          : opts.options
            ? {
                // `valueFn` is a factory PARAMETER (a different function per
                // `_row(...)` caller — Country/Timezone below) read from a
                // nested closure (`value`/`onCreate` bodies) rather than being
                // the top-level prop value itself — that's exactly the shape
                // frank's serializer cannot survive (closures don't travel
                // with the emitted function string). Declared here via
                // `scope:` instead so it rides with the element and is read
                // via `el.scope.valueFn` — the framework's own documented fix
                // for this class (`tickets/fable.md` "decompose workspace-
                // shell's 507 defects", `unresolvable-scope-read` on
                // `components.MemberProfile...EditView.{value,onCreate}`).
                //
                // `opts` rides the same way, for the SAME reason — `children`/
                // `onChange` below close over the `_row(...)` factory
                // parameter directly. The gate previously read this as clean
                // (`scope-read-name-collision` false green: an unrelated
                // module's own `opts` key happened to be present in the
                // emitted globalScope, so `__scope.opts` resolved to THAT
                // object instead of throwing) — both Options selects rendered
                // an unrelated widget spec's fields, silently, at any
                // frank-served runtime. Fixed at the source (smbls
                // `20749e2ce`, the gate's false-green fix), not papered over.
                scope: { valueFn, opts },
                hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', findMe(s)),
                tag: 'select',
                fontSize: 'A',
                fontWeight: '700',
                color: opts.accent || 'title',
                // The kit's FIELD surface (see the Team select below).
                theme: 'field',
                round: 'radiusControl',
                minHeight: 'controlH',
                padding: '0 A',
                cursor: 'pointer',
                width: 'F',
                // focus: THE global ring only (brand reset.js, §8.2) — the old
                // `:focus` border-swap to blue was a second focus style (F1).
                value: (el, s) => {
                  const v = el.scope.valueFn(el, s)
                  return v === '—' ? '' : v || ''
                },
                // The `onCreate` that wrote `el.node.value` by hand is GONE. It
                // duplicated the controlled `value:` above, it is a forbidden
                // direct node write, and it only ever ran once — smbls
                // re-asserts a controlled value after every option (re)mount
                // (element/src/create.js syncSelectValue), which is strictly
                // more correct when a `options()` factory resolves late.
                //
                // FA808 / Rule 10 + the same missing-`value` defect as the two
                // notification selects: the inline childExtends declared only
                // `tag` and `text`, so every <option> reported its TEXT as its
                // value. Picking "— none —" therefore stored the literal string
                // '— none —' into the profile field instead of null, and the
                // controlled `value: ''` matched no option at all, so the
                // control rendered BLANK for anyone with no value set.
                // LanguageOption declares `value` (see the DigestSelect note for
                // why that component and not a new one).
                childExtends: 'LanguageOption',
                childrenAs: 'state',
                children: (el, s) => {
                  const rowOpts = el.scope.opts
                  const list =
                    typeof rowOpts.options === 'function' ? rowOpts.options() : rowOpts.options
                  return [
                    { value: '', text: el.call('polyglot', 'account.member.select.none') },
                    ...list.map((v) => ({ value: v, text: v }))
                  ]
                },
                onChange: (e, el, s) => {
                  const rowOpts = el.scope.opts
                  if (rowOpts.field) _patchUser(el, s, { [rowOpts.field]: e.target.value || null })
                }
              }
            : {
                // Same closure-capture fix as the Options branch above —
                // `onInput` closes over the `_row(...)` factory parameter.
                scope: { opts },
                hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', findMe(s)),
                tag: 'input',
                type: opts.type || 'text',
                fontSize: 'A',
                fontWeight: '700',
                color: opts.accent || 'title',
                // The kit's FIELD surface (see the Team select below).
                theme: 'field',
                round: 'radiusControl',
                textAlign: 'right',
                minHeight: 'controlH',
                padding: '0 A',
                width: 'F',
                placeholder: opts.placeholder || '',
                // focus: THE global ring only (brand reset.js, §8.2) — the old
                // `:focus` border-swap to blue was a second focus style (F1).
                value: opts.editValueFn || valueFn,
                onInput: (e, el, s) => {
                  const rowOpts = el.scope.opts
                  if (!rowOpts.field) return
                  const parsed = rowOpts.parse ? rowOpts.parse(e.target.value) : e.target.value
                  _patchUser(el, s, { [rowOpts.field]: parsed })
                }
              }
      }
    : _readValue(valueFn)
})

// Card wrapper shorthand — the brand's in-page card (the CommonCard /
// AdminCard surface): the solid `card` theme, `radiusCard`, no stroke.
// It was `radiusSheet` + the `overlay` wash, the overlay-sheet radius on an
// in-page card. One inset for every card on the page (UI symmetry); a phone
// or a narrow slot takes the smaller step, so the 390 px posture keeps 262 px
// of row instead of 212 px (C on both sides measured live, 2026-10-28).
// The measure cap rides the stacks now: a stack is never wider than its
// share of the row, so a label never sits a screen away from its value.
const _card = (content) => ({
  flow: 'y',
  gap: 'A',
  padding: 'B',
  round: 'radiusCard',
  theme: 'card',
  minWidth: '0',
  '@mobileL': { padding: 'A' },
  '@appMobile': { padding: 'A' },
  ...content
})

// ── Exported component ──

// Server role enums -> bundle keys. See the note at the Role row below.
const ROLE_KEYS = {
  owner: 'account.member.role.owner',
  'co-owner': 'account.member.role.coOwner',
  superadmin: 'account.member.role.superadmin',
  admin: 'account.member.role.admin',
  manager: 'account.member.role.manager',
  member: 'account.member.role.member',
  editor: 'account.member.role.editor',
  viewer: 'account.member.role.viewer',
  guest: 'account.member.role.guest'
}

// The person's workspace role code: root.roles.byUser (CU-10 §90), the
// legacy `roles` array, then the server's default.
const _roleOf = (root, person) => {
  const byUser = (root && root.roles && root.roles.byUser) || {}
  return (person && person.userId && byUser[person.userId]) || person?.roles?.[0] || 'member'
}

// An EXPLICIT map, never `'account.member.role.' + role` — a key built by
// concatenation is invisible to grep, to the parity test and to every
// extraction tool (docs/I18N-CONVENTION.md §1.3). A role the server invents
// later passes through polyglot unchanged rather than rendering a raw key.
const _roleLabelKey = (role) => ROLE_KEYS[role] || role || ''

// Access state worth a chip: read-only (inactive) or deactivated (left).
// Active is the normal state and shows no chip.
const _accessKey = (person) => {
  const status = person && person.status
  if (status === 'left') return 'account.member.access.left'
  if (status === 'inactive') return 'account.member.access.inactive'
  return ''
}

// "Position · Team" — the hero's second line. Either half may be missing.
const _heroRoleLine = (person, root) => {
  if (!person) return ''
  // The Team row's own reading: the roster's `team` when the row carries one,
  // else the first real team membership.
  const team =
    person.team !== undefined
      ? person.team
      : person.userId || person.email
        ? primaryTeamName(person, root)
        : null
  return [person.position, team].filter((v) => v && v !== '-').join(' · ')
}

export const MemberProfile = {
  // The page fills its slot (2026-09-03, Nika "content layout adapt to
  // layout - make it full width"): a wrapping row of full-width bands and
  // two-stack bands. The hero and the settings heading take a whole line;
  // each stack pair (ProfileMain + ProfileSide, SettingsMain + SettingsSide)
  // shares a line while the CONTAINER holds H + G + the gap, and stacks
  // one under the other below that — a phone, a narrow pane, the assistant
  // rail open. No viewport query: the flex basis reads the real slot.
  flow: 'x',
  flexWrap: 'wrap',
  // Each stack is as tall as its own cards (`stretch` would pad the
  // shorter stack out to the taller one and draw nothing in it).
  alignItems: 'flex-start',
  gap: 'A',
  width: '100%',
  minWidth: '0',
  ProfileHero: _card({
    attr: { 'data-content-section': 'Profile' },
    // The identity plate heads the page on its own line. It WRAPS: the
    // avatar, the text and the actions share a line while they fit, and the
    // actions drop under the text on a phone — they sat at x 445..741 in a
    // 390 px window before, outside the screen (live 2026-10-28).
    flex: '1 1 100%',
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'B',

    HeroAvatar: {
      align: 'center center',
      // The identity plate of the redesign (DESIGN_SPEC §9 Settings/Account):
      // the fixed `avatarXl` circle (72px — never em, it must not scale with
      // the card's type) on the neutral widget plate, title ink at 500. The
      // `overlayStrong` plate it replaces composited to the window ground in
      // dark — the initials floated with no plate at all (live 2026-09-26).
      boxSize: 'avatarXl',
      minWidth: 'avatarXl',
      round: 'radiusPill',
      color: 'title',
      fontSize: 'C',
      fontWeight: '500',
      // user_profiles table stores the avatar as `avatar` (text). The legacy
      // `avatarUrl` field never existed in the DB — collapse both so Google
      // OAuth avatars (s.avatar = googleusercontent URL) render as images
      // instead of leaking as raw text. Fixes #2245, #2233, #2247.
      background: (el, s) => {
        const url = findMe(s).avatarUrl || findMe(s).avatar
        return url && /^(https?:|\/)/i.test(url) ? 'transparent' : 'widgetPlate'
      },
      backgroundImage: (el, s) => {
        const url = findMe(s).avatarUrl || findMe(s).avatar
        return url && /^(https?:|\/)/i.test(url) ? `url(${JSON.stringify(url)})` : 'none'
      },
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative',
      cursor: (el, s) => (el.call('canEditProfile', findMe(s)) ? 'pointer' : 'default'),
      overflow: 'hidden',
      // The reveal keys off THIS element's hover: ChangeOverlay carries
      // pointer-events:none (clicks fall through to the onClick below), so
      // its own :hover can never match — the old overlay-side ':hover' was
      // dead CSS and the "Change" prompt never appeared over photos.
      ':hover .avatar-change-overlay': { opacity: '1' },
      // Never render the avatar value as text — it's a URL string for Google
      // OAuth users. Show initials only when there is no displayable image.
      text: (el, s) => {
        const url = findMe(s).avatarUrl || findMe(s).avatar
        if (url && /^(https?:|\/)/i.test(url)) return ''
        // Initials fallback when no image is available — by GRAPHEME and
        // letters only (a UTF-16 `p[0]` split a surrogate pair and kept
        // symbols), two letters, one for a Han / Kana / Hangul name, and
        // never re-cased for Georgian (toUpperCase turns Mkhedruli into
        // Mtavruli capitals).
        const name = findMe(s).name || findMe(s).email || ''
        const letters = (w) => Array.from(String(w)).filter((c) => /\p{L}/u.test(c))
        const up = (c) => (/[\u10D0-\u10FF]/.test(c) ? c : c.toUpperCase())
        const parts = String(name)
          .trim()
          .split(/\s+/)
          .filter((w) => letters(w).length)
        const first = parts[0] ? letters(parts[0])[0] : ''
        if (/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u.test(first))
          return first
        if (parts.length >= 2) return up(first) + up(letters(parts[1])[0])
        return letters(name).slice(0, 2).map(up).join('')
      },
      onClick: (e, el, s) => {
        if (!el.call('canEditProfile', findMe(s))) return
        const input = el.AvatarUpload?.node
        if (input) input.click()
      },
      ChangeOverlay: {
        // Hidden when user cannot edit. HOVER-ONLY in BOTH states
        // (2026-09-02 per Nika "fix settings ui"): the old always-on prompt
        // for avatar-less users painted the black.5 scrim + "Upload photo"
        // permanently OVER the initials — two text layers stacked into an
        // illegible blob on the very first screen of Settings. The initials
        // now stay clean; the scrim appears on hover, in both the
        // no-avatar ("Upload photo") and has-avatar ("Change") states —
        // the cursor:pointer on HeroAvatar still advertises the action.
        // The scrim itself stays FIXED black.5/white: it overlays the
        // user's arbitrary uploaded photo (same scrim-over-imagery
        // contract as a lightbox badge).
        hide: (el, s) => !el.call('canEditProfile', findMe(s)),
        class: 'avatar-change-overlay',
        position: 'absolute',
        inset: '0',
        align: 'center center',
        background: 'black.5',
        color: 'white',
        fontSize: 'Z',
        fontWeight: '700',
        opacity: '0',
        transition: 'opacity A defaultBezier',
        pointerEvents: 'none',
        text: (el, s) => {
          const url = findMe(s).avatarUrl || findMe(s).avatar
          return el.call(
            'polyglot',
            url && /^(https?:|\/)/i.test(url)
              ? 'account.member.avatar.change'
              : 'account.member.avatar.upload'
          )
        }
      },
      AvatarUpload: {
        tag: 'input',
        type: 'file',
        accept: 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
        display: 'none',
        onChange: async (e, el, s) => {
          const file = e.target.files?.[0]
          if (!file) return
          try {
            if (typeof el.sdk?.uploadFile !== 'function') {
              el.call('openNotification', {
                type: 'error',
                title: el.call('polyglot', 'account.member.uploadUnavailable.title'),
                message: el.call('polyglot', 'account.member.uploadUnavailable.message')
              })
              return
            }
            const res = await el.sdk.uploadFile(file)
            // FileController.formatFileResponse returns { src, urls: { absolute: { publicDownload } } }.
            // Also handle legacy shapes { url } / { data.url } / { publicUrl } from other environments.
            const url =
              res?.src ||
              res?.urls?.absolute?.publicDownload ||
              res?.url ||
              res?.data?.url ||
              res?.publicUrl ||
              (typeof res === 'string' ? res : null)
            if (!url) throw new Error('Upload returned no URL')
            _patchUser(el, s, { avatar: url })

            // Optimistically reflect it locally so the image updates instantly without a refresh
            const myEmail = s.root.currentUserEmail
            s.root.update({
              people: (s.root.people || []).map((p) =>
                p.email === myEmail ? { ...p, avatar: url } : p
              )
            })
          } catch (err) {
            console.warn('[MemberProfile] avatar upload failed', err)
            el.call('openNotification', {
              type: 'error',
              title: el.call('polyglot', 'account.member.upload.failed'),
              message: err?.message || String(err)
            })
          } finally {
            // Reset so picking the same file twice still fires onChange.
            if (e.target) e.target.value = ''
          }
        }
      }
    },
    HeroInfo: {
      flow: 'y',
      gap: 'Y',
      // The text column grows into the free space and may shrink below its
      // longest word: a long name or address wraps inside the card instead
      // of pushing the actions out of it (hero scrollWidth 694 px in a
      // 296 px card at 390, live 2026-10-28).
      flex: '1 1 var(--spacing-G)',
      minWidth: '0',
      HeroName: {
        tag: 'h2',
        fontSize: 'C',
        fontWeight: '700',
        color: 'title',
        letterSpacing: '-0.02em',
        overflowWrap: 'anywhere',
        // Hide until the person record has loaded — avoids the "Unknown"
        // flash that occurs when findMe(s) returns {} before people hydrates.
        hide: (el, s) => !findMe(s).name,
        text: (el, s) => findMe(s).name || ''
      },
      // Position and primary team on one line: what the person does and
      // where. Either half may be missing; the line hides when both are.
      HeroPosition: {
        tag: 'span',
        fontSize: 'A',
        color: 'paragraph',
        overflowWrap: 'anywhere',
        hide: (el, s) => !_heroRoleLine(findMe(s), s.root),
        text: (el, s) => _heroRoleLine(findMe(s), s.root)
      },
      HeroMeta: {
        flow: 'x',
        flexWrap: 'wrap',
        align: 'center',
        gap: 'Y Z1',
        padding: 'X 0 0',
        // The workspace role, for every viewer: the People page already
        // shows it on each card. A neutral chip, never a hue (no status).
        HeroRole: {
          extends: 'AppStatusChip',
          hide: (el, s) => !findMe(s).userId,
          label: (el, s) => _roleLabelKey(_roleOf(s.root, findMe(s)))
        },
        // Access state, only when it is not the normal one: read-only is a
        // warning, deactivated (left) is a danger. Active shows nothing.
        HeroAccess: {
          extends: 'AppStatusChip',
          hide: (el, s) => !_accessKey(findMe(s)),
          status: (el, s) => (findMe(s).status === 'left' ? 'danger' : 'warning'),
          label: (el, s) => _accessKey(findMe(s))
        },
        HeroEmail: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          overflowWrap: 'anywhere',
          minWidth: '0',
          // The signed-in email is the fallback for the OWN profile only; it
          // is read up front so the signal is tracked on every run (FA515).
          text: (el, s) => {
            const ownEmail = s.root.currentUserEmail
            return findMe(s).email || (el.call('isSelf', s) ? ownEmail : '') || ''
          }
        }
      }
    },

    // The actions share the hero line while they fit and drop under the
    // text on a narrow card. The shared capsule kit owns their geometry and
    // states (Rule 68: no padding, height or type at the call site): the ink
    // capsule for the one primary action, the tonal capsule beside it, the
    // text capsule for Cancel.
    HeroActions: {
      // Show when the current user can edit the profile (self / admin)
      // OR when viewing someone else's profile (to show the Message button).
      hide: (el, s) => {
        const canEdit = el.call('canEditProfile', findMe(s))
        const isSelfView = el.call('isSelf', s)
        return !canEdit && isSelfView
      },
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Z',
      align: 'center',

      MessageBtn: {
        extends: 'AppCapsuleBtnPrimary',
        // Only show when viewing someone else's profile.
        hide: (el, s) => el.call('isSelf', s),
        text: (el) => el.call('polyglot', 'account.member.action.message'),
        onClick: (e, el, s) => {
          const email = findMe(s).email
          if (!email) return
          const root = s.root
          root.update({ _pendingDmEmail: email })
          const rootEl = el.getRoot()
          el.router('/chat', rootEl)
        }
      },

      EditBtn: {
        extends: 'AppCapsuleBtnToolbar',
        // The fields it opens live on the profile, never on /settings.
        hide: (el, s) => {
          const editing = !!s.root?._profileEditing
          const view = el.call('memberProfileView', s)
          return editing || view === 'settings' || !el.call('canEditProfile', findMe(s))
        },
        text: (el) => el.call('polyglot', 'account.member.action.edit'),
        onClick: (e, el, s) =>
          s.root.update({
            _profileEditing: true,
            _profileBackup: JSON.stringify(findMe(s))
          })
      },

      // The switch between the two views of your own page (stage 3,
      // memberProfileAccess.js memberProfileView): the profile offers your
      // settings, the settings offer your profile. It opens the way the
      // avatar menu opens Settings (accountMenu.js openAccountPage): inside a
      // pane, that pane. Hidden while an edit is open (Cancel and Save own
      // the row then), and where the host serves no such route (canvas
      // serves /settings only). Every signal is read up front (FA515).
      SettingsBtn: {
        extends: 'AppCapsuleBtnToolbar',
        hide: (el, s) => {
          const editing = !!s.root?._profileEditing
          const view = el.call('memberProfileView', s)
          const pages = (el.context && el.context.pages) || {}
          return editing || view === 'settings' || !el.call('isSelf', s) || !pages['/settings']
        },
        text: (el) => el.call('polyglot', 'account.member.action.settings'),
        onClick: (e, el) => el.call('openAccountPage', '/settings')
      },
      ViewProfileBtn: {
        extends: 'AppCapsuleBtnToolbar',
        hide: (el, s) => {
          const view = el.call('memberProfileView', s)
          const pages = (el.context && el.context.pages) || {}
          return view !== 'settings' || !pages['/profile']
        },
        text: (el) => el.call('polyglot', 'account.member.action.viewProfile'),
        onClick: (e, el) => el.call('openAccountPage', '/profile')
      },

      CancelBtn: {
        extends: 'AppCapsuleBtnGhost',
        hide: (el, s) => !s.root?._profileEditing,
        text: (el) => el.call('polyglot', 'account.member.action.cancel'),
        onClick: (e, el, s) => {
          if (s.root._profileBackup) {
            const original = JSON.parse(s.root._profileBackup)
            s.root.update({
              people: (s.root.people || []).map((p) =>
                p.userId === original.userId ? original : p
              ),
              peopleAll: (s.root.peopleAll || []).map((p) =>
                p.userId === original.userId ? original : p
              )
            })
          }
          for (const field of Object.keys(_pendingUserPatches)) {
            clearTimeout(_patchTimers[field])
            delete _patchTimers[field]
            delete _pendingUserPatches[field]
          }
          s.root.update({ _profileEditing: false })
        }
      },

      SaveBtn: {
        extends: 'AppCapsuleBtnPrimary',
        hide: (el, s) => !s.root?._profileEditing,
        // Busy = the real disabled attribute (the kit's `field-disabled`
        // step), never a faded opacity; the label stack keeps the width of
        // the longer label so the capsule does not resize (Rule 66).
        disabled: (el, s) => !!s.root?._profileSaving,
        'aria-busy': (el, s) => !!s.root?._profileSaving,
        AppLabelStack: {
          labels: ['account.member.team.save', 'account.member.team.saving'],
          shown: (el, s) => (s.root?._profileSaving ? 1 : 0)
        },
        onClick: async (e, el, s) => {
          if (s.root?._profileSaving) return
          s.root.update({ _profileSaving: true })
          try {
            await _flushPendingPatches(el)
          } finally {
            s.root.update({ _profileSaving: false, _profileEditing: false })
          }
        }
      }
    }
  }),

  // ── The profile: two stacks, not one auto-fit grid ──
  // The 2026-09-03 auto-fit grid paired unrelated cards in one row, so the
  // shorter card left a hole as tall as the difference (live 2026-10-28,
  // 1920 window: Details 912 px beside Documents 254 px = a 658 px hole).
  // Two flex stacks share the row instead: the wide one holds the reading
  // cards, the narrow one the private side cards. Each stack is as tall as
  // its own cards. The row wraps on the CONTAINER width (H + G + gap), so a
  // narrow slot or a phone gets one stack under the other with no media
  // query, and the wide stack never stretches a row past its measure.
  ProfileMain: {
    // The profile view only — /settings shows the settings stacks below.
    hide: (el, s) => el.call('memberProfileView', s) === 'settings',
    flow: 'y',
    gap: 'A',
    flex: '2 1 var(--spacing-H)',
    minWidth: '0',
    ProfileDetails: _card({
      attr: { 'data-content-section': 'Details' },

      DetailsTitle: {
        extends: 'AppMicroLabel',
        text: '{{ account.profile.sections.details | polyglot }}'
      },

      FullName: _row(
        '{{ account.member.field.fullName | polyglot }}',
        (el, s) => findMe(s).name || '—',
        {
          editable: true,
          field: 'name',
          placeholder: (el) => el.call('polyglot', 'account.member.placeholder.name')
        }
      ),
      Position: _row(
        '{{ account.member.field.position | polyglot }}',
        (el, s) => findMe(s).position || '—',
        {
          editable: true,
          field: 'position',
          placeholder: (el) => el.call('polyglot', 'account.member.placeholder.position'),
          editValueFn: (el, s) => findMe(s).position || ''
        }
      ),
      // Team picker — reads from `root.teamsByEmail` (Mongo-sourced via
      // `functions/teams.js`). Writes go through SDK (add/remove Team
      // members). No user_profiles text fallback — legacy columns dropped.
      Team: {
        // The `_row()` shape, inline — this row has a bespoke team picker.
        flow: 'x',
        flexWrap: 'wrap',
        align: 'baseline space-between',
        gap: 'X B',
        padding: 'Z 0',
        minWidth: '0',
        RowLabel: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: '{{ account.member.field.team | polyglot }}'
        },
        RowValue: {
          flow: 'x',
          align: 'center right',
          gap: 'Z',
          minWidth: '0',
          ReadView: {
            ..._readValue((el, s) => {
              const me = findMe(s)
              if (me.team !== undefined) return me.team || ''
              return primaryTeamName(me, s.root) || ''
            }),
            hide: (el, s) => !!s.root?._profileEditing && el.call('canEditProfile', findMe(s))
          },
          EditSelect: {
            hide: (el, s) => !s.root?._profileEditing || !el.call('canEditProfile', findMe(s)),
            tag: 'select',
            fontSize: 'A',
            fontWeight: '700',
            color: 'title',
            // The kit's FIELD surface (its own fill + hover/focus steps, no
            // hairline edge — the borderless law); was `dialog` + a hairline.
            theme: 'field',
            round: 'radiusControl',
            minHeight: 'controlH',
            padding: '0 A',
            cursor: 'pointer',
            minWidth: 'F',
            value: (el, s) => {
              const me = findMe(s)
              if (me.team !== undefined) return me.team || ''
              return primaryTeamName(me, s.root) || ''
            },
            // FA808 / Rule 10 — the inline `childExtends: {…}` object cannot be
            // carried by frank's serializer, so the published build rendered an
            // option-less select. This one already declared `value`, so unlike
            // its neighbours it stored the right code; the shape is otherwise
            // identical to LanguageOption, which is why that component is reused
            // here too (see the DigestSelect note).
            childExtends: 'LanguageOption',
            childrenAs: 'state',
            children: (el, s) => {
              const orgId =
                s.root?.jwtClaims?.workspace_org_id || (s.root?.jwtClaims?.orgs || [])[0]?.id
              const list = teamsInOrg(orgId, s.root)
              return [
                { value: '', text: el.call('polyglot', 'account.member.select.none') },
                ...list.map((t) => ({ value: t.name, text: t.name })),
                { value: '__create__', text: el.call('polyglot', 'account.member.team.create') }
              ]
            },
            onChange: async (e, el, s) => {
              const me = findMe(s)
              if (!me?.userId || !me?.email) return
              const root = s.root
              const orgId =
                root?.jwtClaims?.workspace_org_id || (root?.jwtClaims?.orgs || [])[0]?.id
              if (!orgId) return
              let value = e.target.value

              if (value === '__create__') {
                const name =
                  typeof window !== 'undefined'
                    ? window.prompt(el.call('polyglot', 'account.member.team.newPrompt'))
                    : null
                const currentName = me.team !== undefined ? me.team : primaryTeamName(me, root)
                if (!name) {
                  if (el.node) el.node.value = currentName || ''
                  return
                }
                try {
                  const team = await createTeam(root, { orgId, name: name.trim() })
                  value = team.name
                } catch (err) {
                  console.warn('Team creation failed', err)
                  if (el.node) el.node.value = currentName || ''
                  return
                }
              }

              _patchUser(el, s, { team: value })

              if (el.node) {
                el.node.value = value || ''
                typeof window !== 'undefined' &&
                  window.requestAnimationFrame(() => {
                    if (el.node) el.node.value = value || ''
                  })
              }
            }
          }
        }
      },
      Country: _row(
        '{{ account.member.field.country | polyglot }}',
        (el, s) => _countryOf(findMe(s)) || '—',
        {
          editable: true,
          field: 'country',
          combobox: true,
          placeholder: (el) => el.call('polyglot', 'account.member.field.country'),
          options: _countryOptions
        }
      ),
      City: _row(
        '{{ account.member.field.city | polyglot }}',
        (el, s) => _cityOf(findMe(s)) || '—',
        {
          editable: true,
          field: 'city',
          placeholder: (el) => el.call('polyglot', 'account.member.placeholder.city'),
          editValueFn: (el, s) => _cityOf(findMe(s)) || ''
        }
      ),
      Timezone: _row(
        '{{ account.member.field.timezone | polyglot }}',
        (el, s) => findMe(s).timezone || '—',
        {
          editable: true,
          field: 'timezone',
          combobox: true,
          placeholder: (el) => el.call('polyglot', 'account.member.field.timezone'),
          options: _timezoneOptions
        }
      ),
      GitHub: _row(
        '{{ account.member.field.github | polyglot }}',
        (el, s) =>
          findMe(s).github || (el.call('isSelf', s) && s.root?.user?.profile?.github) || '—',
        {
          editable: true,
          field: 'github',
          placeholder: (el) => el.call('polyglot', 'account.member.placeholder.github'),
          editValueFn: (el, s) =>
            findMe(s).github || (el.call('isSelf', s) && s.root?.user?.profile?.github) || ''
        }
      ),
      // Account-plane row (INVITE-ONBOARDING-USERNAME-NOT-ORG-1): the platform
      // username lives on the USER record (sdk.updateMe), not the roster doc
      // `_patchUser` writes — hand-rolled instead of `opts.field` so the save
      // routes to the right plane. Self-edit only; saves on change (blur/
      // Enter), never per keystroke — a half-typed handle must not land.
      Username: {
        flow: 'x',
        flexWrap: 'wrap',
        align: 'baseline space-between',
        gap: 'X B',
        padding: 'Z 0',
        minWidth: '0',
        RowLabel: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: '{{ account.member.field.username | polyglot }}'
        },
        RowValue: {
          flow: 'x',
          align: 'center right',
          minWidth: '0',
          ReadView: {
            // The signed-in user's own handle is the fallback for the OWN
            // profile only. On another member's profile it printed the
            // VIEWER's username (qa.employee read `nikoloza`, live
            // 2026-10-28): the viewed person's record has no username field.
            ..._readValue(
              (el, s) =>
                findMe(s).username ||
                (el.call('isSelf', s) ? s.root?.currentUserUsername : '') ||
                ''
            ),
            hide: (el, s) =>
              !!s.root?._profileEditing && findMe(s)?.email === s.root?.currentUserEmail
          },
          EditView: {
            hide: (el, s) =>
              !s.root?._profileEditing || findMe(s)?.email !== s.root?.currentUserEmail,
            tag: 'input',
            type: 'text',
            fontSize: 'A',
            fontWeight: '700',
            color: 'title',
            // The kit's FIELD surface (its own fill + hover/focus steps, no
            // hairline edge — the borderless law); was `dialog` + a hairline.
            theme: 'field',
            round: 'radiusControl',
            textAlign: 'right',
            minHeight: 'controlH',
            padding: '0 A',
            width: 'F',
            placeholder: (el) => el.call('polyglot', 'account.member.placeholder.github'),
            maxlength: 39,
            value: (el, s) => findMe(s).username || s.root?.currentUserUsername || '',
            onChange: async (e, el, s) => {
              const next = String(e.target?.value || '')
                .trim()
                .toLowerCase()
              const current = s.root?.currentUserUsername || ''
              if (!next || next === current) return
              try {
                await el.sdk.updateMe({ username: next })
                s.root?.update({ currentUserUsername: next })
              } catch (err) {
                console.warn('[MemberProfile] username update failed:', err?.message || err)
                if (el.node) el.node.value = current
              }
            }
          }
        }
      },
      Tenure: _row('{{ account.member.field.tenure | polyglot }}', (el, s) =>
        _tenure(findMe(s).startDate)
      ),
      Role: _row(
        '{{ account.member.field.role | polyglot }}',
        (el, s) => {
          // CU-10 §90: roles no longer on person. Look up from root.roles.byUser.
          const me = findMe(s)
          const userId = me?.userId
          const byUser = s.root?.roles?.byUser || {}
          const role = (userId && byUser[userId]) || me?.roles?.[0] || 'member'
          // An EXPLICIT map, never `'account.member.role.' + role` — a key built
          // by concatenation is invisible to grep, to the parity test and to
          // every extraction tool (docs/I18N-CONVENTION.md §1.3). A role the
          // server invents later falls through polyglot unchanged rather than
          // rendering a raw key.
          return el.call('polyglot', ROLE_KEYS[role] || role)
        },
        { hide: (el) => !el.call('isSuperAdmin') }
      )
    }),
    ProfileAvailability: {
      ..._card({}),
      attr: { 'data-content-section': 'Availability' },
      // THE CARD'S OWN LOADING MARK (stage 3): the account id whose
      // availability has come back. The card said "No availability set" and
      // painted an empty grid until its data arrived, then switched to the
      // real schedule (live 2026-10-28). Until the mark matches the viewed
      // person, the summary is a skeleton line, the grid is busy and inert,
      // and Reset / Select all wait: a click there saved a grid built from
      // nothing over the stored one. A name the root never owns, so a read
      // never falls through to a root key.
      state: { _availFor: '' },
      scope: {
        // Loading: the viewed person has an account id and this card's load
        // for that id has not come back yet. The children read it through
        // their inherited scope.
        availLoading(s) {
          const id = findMe(s).userId
          return !!id && s._availFor !== id
        }
      },

      // Everyone on the team, the owner included — one rule with the
      // section rails (memberProfileAccess.js showsAvailability).
      hide: (el, s) => !el.call('showsAvailability', findMe(s), s.root),
      // Pull the viewed person's server-side AvailabilityRule into root.people
      // so the grid + summary show persisted data (registered via the calendar
      // eager slice folded into the shell context). Once per person; the mark
      // lands when THAT load settles, found or not (a host without the
      // calendar slice answers undefined, which settles at once). The card
      // may show someone else by then: only the person it asked for counts.
      onRender: (el, s) => {
        const person = findMe(s)
        const id = person && person.userId
        if (!id || el._hydrated === id) return
        el._hydrated = id
        const done = () => {
          if (el._hydrated === id && el.state) el.state.update({ _availFor: id })
        }
        Promise.resolve(el.call('hydratePersonAvailability', s.root, person)).then(done, done)
      },
      AvailHeader: {
        flow: 'x',
        // WRAPS: at 390 px the two buttons squeezed the title to one letter
        // per line (212 x 287 px, live 2026-10-28). The buttons drop under
        // the title when the line is short.
        flexWrap: 'wrap',
        gap: 'Z A',
        align: 'center space-between',
        // Section heads are the mono micro-label (DESIGN_SPEC §5.7).
        AvailTitle: {
          extends: 'AppMicroLabel',
          text: '{{ account.member.availability.title | polyglot }}'
        },
        AvailActions: {
          flow: 'x',
          gap: 'A',
          AvailReset: {
            // The tonal capsule of the kit, compact in a card head (Rule 68:
            // the kit owns the height and inset).
            extends: 'AppCapsuleBtnToolbar',
            compact: true,
            disabled: (el, s) => el.scope.availLoading(s),
            text: (el) => el.call('polyglot', 'account.member.availability.reset'),
            hide: (el, s) => {
              if (!s.root?.currentUserEmail) return true
              return !(el.call('isSelf', s) || el.call('canAdmin', s))
            },
            onClick: (e, el, s) => {
              const root = s.root || el.state?.root
              const user = el.call('findMe', s)
              if (!user) return

              const emptyGrid = Array.from({ length: 7 }, () =>
                Array.from({ length: 24 }, () => false)
              )
              const next = (root.people || []).map((p) =>
                p.email === user.email ? { ...p, availabilityGrid: emptyGrid } : p
              )
              root.update({ people: next })
              el.call('saveAvailabilityGrid', root, user, emptyGrid)
            }
          },
          AvailSelectAll: {
            // The tonal capsule of the kit, compact in a card head (Rule 68:
            // the kit owns the height and inset).
            extends: 'AppCapsuleBtnToolbar',
            compact: true,
            disabled: (el, s) => el.scope.availLoading(s),
            text: (el) => el.call('polyglot', 'account.member.availability.selectAll'),
            hide: (el, s) => {
              if (!s.root?.currentUserEmail) return true
              return !(el.call('isSelf', s) || el.call('canAdmin', s))
            },
            onClick: (e, el, s) => {
              const root = s.root || el.state?.root
              const user = el.call('findMe', s)
              if (!user) return

              // Flip all cells in grid to on
              const fullGrid = Array.from({ length: 7 }, () =>
                Array.from({ length: 24 }, () => true)
              )

              const next = (root.people || []).map((p) =>
                p.email === user.email ? { ...p, availabilityGrid: fullGrid } : p
              )
              root.update({ people: next })
              el.call('saveAvailabilityGrid', root, user, fullGrid)
            }
          }
        }
      },
      // Schedule-derived, tz-honest one-liner — the read-only availability view
      // when looking at another member ("Available now · until 18:00 · …").
      AvailSummary: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'paragraph',
        hide: (el, s) => el.scope.availLoading(s),
        text: (el, s) => el.call('availabilitySummaryText', findMe(s))
      },
      // The summary's place while the data is on its way: one skeleton line
      // of the summary's own height, so the swap moves nothing (Rule 66),
      // read out as loading. The bare key reuses the shared SkeletonRow.
      SkeletonRow: {
        width: '60%',
        height: 'A2',
        role: 'status',
        aria: { label: (el) => el.call('polyglot', 'common.state.loading') },
        hide: (el, s) => !el.scope.availLoading(s)
      },
      // Busy and inert until the data arrives: no pointer, no keyboard, and
      // the cells lose their hue (an all-red grid read as "never available").
      ProfileAvailabilityGrid: {
        aria: { busy: (el, s) => (el.scope.availLoading(s) ? 'true' : null) },
        inert: (el, s) => (el.scope.availLoading(s) ? '' : null),
        '&[aria-busy="true"]': {
          filter: 'grayscale(1)',
          animation: 'pulse 1.5s ease-in-out infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
        }
      }
    }
  },
  ProfileSide: {
    flow: 'y',
    gap: 'A',
    flex: '1 1 var(--spacing-G)',
    minWidth: '0',
    // Empty stack = no column: every side card is private (self or a holder
    // of profile.private.read) or the superadmin access card on someone
    // else's profile. A hidden stack lets the main stack take the row.
    // The profile view only, like its partner stack.
    hide: (el, s) => {
      const settings = el.call('memberProfileView', s) === 'settings'
      return (
        settings ||
        (!el.call('canAccessPrivate', findMe(s)) &&
          (el.call('isSelf', s) || !el.call('canSuperadmin', s)))
      )
    },
    ProfileChecklist: {
      ..._card({}),
      attr: { 'data-content-section': 'Checklist' },
      hide: (el, s) => !el.call('canAccessPrivate', findMe(s)),

      CheckHead: {
        flow: 'y',
        gap: 'Z',
        // The label reads in neutral ink (orange as small-text ink measured
        // 2.4:1 on the light stage); the hue stays on the status dots.
        CheckEyebrow: {
          extends: 'AppMicroLabel',
          text: '{{ account.profile.sections.checklist | polyglot }}'
        }
      },

      CheckList: {
        flow: 'y',
        gap: 'Y',
        childExtends: 'MemberProfileCheckItem',
        childrenAs: 'state',
        children: (el, s) => {
          const p = findMe(s)
          // This is the general account checklist. Employment documents,
          // equity and developer accounts are not universal requirements;
          // actual documents and equity already have their own sections.
          return [
            {
              label: 'account.member.checklist.location',
              done: !!(p.location && p.timezone)
            },
            { label: 'account.member.checklist.availability', done: !!p.workingDays }
          ]
        }
      }
    },
    ProfileDocuments: {
      ..._card({}),
      attr: { 'data-content-section': 'Documents' },
      hide: (el, s) => !el.call('canAccessPrivate', findMe(s)),

      DocHead: {
        flow: 'y',
        gap: 'Z',
        DocEyebrow: {
          extends: 'AppMicroLabel',
          text: '{{ account.member.documents.title | polyglot }}'
        }
      },

      // The OWN documents only. `docs.userDocs.list` is scoped to the CALLER
      // on the server, and `root.userDocs` holds the caller's documents too,
      // so on another member's profile both printed the VIEWER's signed
      // documents under that member's name. Until the server can list a
      // named member's documents for a `profile.private.read` holder, another
      // member's card shows the empty state instead of the wrong person's
      // files.
      DocList: {
        flow: 'y',
        gap: 'X',
        childExtends: 'MemberProfileDocRow',
        childrenAs: 'state',
        children: (el, s) =>
          (el.call('isSelf', s) ? s.root?.userDocs || [] : []).map((d) => ({
            id: d.id,
            title: d.title,
            size: d.file_size ? Math.round(d.file_size / 1024) : null,
            signedAt: d.signed_at || '',
            storagePath: d.storage_path
          }))
      },
      // The empty state — a sentence in caption ink, not a fake document row.
      DocEmpty: {
        tag: 'p',
        hide: (el, s) => el.call('isSelf', s) && (s.root?.userDocs || []).length > 0,
        fontSize: 'Z2',
        color: 'caption',
        text: (el) => el.call('polyglot', 'account.member.documents.empty')
      }
    }
  },
  // ── Settings: the owner's own preferences, on their own page ──
  // /settings (stage 3, memberProfileAccess.js memberProfileView): same
  // two-stack shape as the profile above, and the page's own title names it,
  // so no heading sits inside. The view is the owner's only (another
  // member's page is always the profile). The cards are assigned below
  // (MemberProfile.SettingsMain.X = …); the shared page mounts the Mail card
  // at the end of SettingsMain.
  SettingsMain: {
    hide: (el, s) => el.call('memberProfileView', s) !== 'settings',
    flow: 'y',
    gap: 'A',
    flex: '2 1 var(--spacing-H)',
    minWidth: '0'
  },
  SettingsSide: {
    hide: (el, s) => el.call('memberProfileView', s) !== 'settings',
    flow: 'y',
    gap: 'A',
    flex: '1 1 var(--spacing-G)',
    minWidth: '0'
  }
}

const _selfCard = (content) => ({
  ..._card(content),
  hide: (el) => !el.call('isSelf')
})

// THE one write funnel for this page's preferences (digest, mentions,
// birthday reminders, notification sound).
//
// It also REPAIRS legacy rows for the two enum keys. Until the notification
// selects declared a real `value` on their options, an <option> reported its
// TEXT as its value, so `e.target.value` was the capitalised LABEL and this
// function persisted 'Daily' / 'Realtime' instead of 'daily' / 'realtime'.
// (smbls documents the same trap at element/src/create.js — "the browser fell
// back to the option's TEXT as its value".)
//
// Every legal code is exactly the lowercase of its own label, because the
// labels were GENERATED from the codes by `v.charAt(0).toUpperCase() +
// v.slice(1)`. So `toLowerCase()` is the exact inverse of the bug, not a
// guess. Normalising the MERGED object — not only the patch — means any write
// through this funnel repairs both keys at once: a person who never touches
// the digest select still converges the moment they flip the birthday toggle.
// The read side (the selects' `scope.current`) is tolerant meanwhile, so a
// legacy row shows the right option immediately, before any write happens.
//
// Safe by construction: nothing outside this file compares these values.
// PrefsService round-trips the whole object through a Mongo `Mixed` subdoc
// (server core/models/UserPreference.js) and no digest/notification consumer
// reads them, so lowercasing is idempotent on correct rows and repairs broken
// ones.
const _savePrefs = async (root, patch) => {
  if (!root?.currentUserId) return
  const prefs = { ...(root.userPrefs || {}), ...patch, user_id: root.currentUserId }
  for (const key of ['email_digest', 'mention_notifications']) {
    if (typeof prefs[key] === 'string') prefs[key] = prefs[key].toLowerCase()
  }
  root.update({ userPrefs: prefs })
  try {
    await getSDK().execute('workspaceProject.userPreferences', 'update', { payload: prefs })
  } catch (e) {
    console.warn('prefs save failed', e)
  }
}

// ── Accessibility (self only) ──
//
// The same four choices the public site offers (company/packages/landing —
// its panel is the reference), on the surface a signed-in person actually
// reaches (2026-09-04 per Nika "add same accessibility options as we have in
// landing also in workspace settings").
//
// PER PERSON, on this device, exactly like the theme — not a workspace-wide
// admin default. (The interface LANGUAGE is the opposite: since 2026-09-10 it
// is the workspace's own setting, and this page carries no control for it.) An admin forcing reduced motion or high
// contrast on everyone would be the opposite of an accessibility feature; the
// choice belongs to the person who needs it. `_selfCard` gates it the same
// way, because this page also renders OTHER people's profiles.
//
// Every row writes through ONE function, `setA11yPref`
// (shared/functions/a11yPrefs.js), which validates the key, persists the whole
// set and updates root state; the shell mirrors the result onto <html> and its
// injected sheet carries the rules. So this card is chrome only — nothing here
// knows what "high contrast" does, which is what keeps the site and the app
// from drifting apart.
//
// Labels take the FUNCTION form, like every other card on this page: a
// `{{ key | polyglot }}` template renders EMPTY under canvas, which registers
// no `context.polyglot` (see LanguageSelectField.js's note).
MemberProfile.SettingsMain.ProfileAccessibility = _selfCard({
  attr: { 'data-content-section': 'Accessibility' },
  gap: 'B',
  AccessibilityTitle: {
    tag: 'h2',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    text: (el) => el.call('polyglot', 'account.member.a11y.title')
  },
  AccessibilityHint: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'caption',
    text: (el) => el.call('polyglot', 'account.member.a11y.hint')
  },

  AccessibilityRows: {
    flow: 'y',

    // TYPE SCALE — three chips, not a switch: it is one choice among three,
    // and a person needs to see which step they are on. `aria-pressed` on
    // each, so a screen reader announces the state rather than the glyph.
    FontScale: {
      extends: 'MemberProfileA11yRow',
      RowLabel: {
        LabelText: { text: (el) => el.call('polyglot', 'account.member.a11y.fontSize') },
        Hint: { text: (el) => el.call('polyglot', 'account.member.a11y.fontSizeHint') }
      },
      Control: {
        flow: 'x',
        gap: 'Y',
        flexShrink: '0',
        // The row recipe's own `Control.SwitchInput` is inherited here and has
        // to be retired explicitly: this row is three chips, not a switch.
        // Without the tombstone it rendered a FOURTH control — and, because
        // `childExtends` applies to every child of Control, the leftover
        // switch was ALSO extended into a chip, so it painted an empty
        // `aria-pressed="true"` button beside the three real ones (measured
        // live 2026-09-04: 4 buttons, 4 switches, one with no label).
        SwitchInput: null,
        childExtends: 'MemberProfileA11yChip',
        // Each chip NAMES its own step. All three carried the row's name
        // ("Text size") over the same visible "A", so a screen reader heard one
        // sentence three times and only `aria-pressed` differed.
        Smaller: {
          scope: { pref: 'sm', labelKey: 'account.member.a11y.fontSizeSmall' },
          text: 'A',
          fontSize: 'Z1'
        },
        Normal: {
          scope: { pref: 'md', labelKey: 'account.member.a11y.fontSizeNormal' },
          text: 'A',
          fontSize: 'A'
        },
        Larger: {
          scope: { pref: 'lg', labelKey: 'account.member.a11y.fontSizeLarge' },
          text: 'A',
          fontSize: 'B'
        }
      }
    },

    Contrast: {
      extends: 'MemberProfileA11yRow',
      scope: {
        key: 'a11yContrast',
        labelKey: 'account.member.a11y.contrast',
        hintKey: 'account.member.a11y.contrastHint'
      },
      Control: { SwitchInput: {} }
    },

    Motion: {
      extends: 'MemberProfileA11yRow',
      scope: {
        key: 'a11yMotion',
        labelKey: 'account.member.a11y.motion',
        hintKey: 'account.member.a11y.motionHint'
      },
      Control: { SwitchInput: {} }
    },

    Links: {
      extends: 'MemberProfileA11yRow',
      scope: {
        key: 'a11yLinks',
        labelKey: 'account.member.a11y.links',
        hintKey: 'account.member.a11y.linksHint'
      },
      Control: { SwitchInput: {} }
    },

    // SINGLE-KEY SHORTCUTS — the off switch WCAG 2.1.4 asks for
    // (HOME_AMBIENT_SKIN_SPEC §5, DESIGN-WL-SHELL-KEYBOARD-MOTION-1): keys
    // like `/`, `?`, `J`, `G then H` run commands when focus is not in a
    // field; a speech or switch user can turn them off, and the chords
    // (⌘K and the like) stay. ON by default.
    //
    // Unlike the four rows above (device-local, `setA11yPref`) this is the
    // person's ACCOUNT preference — `user_preferences.shortcutsSingleKeys`
    // (root.userPrefs), which the shortcut registry reads — so it follows
    // them to every device. The write spreads the current row, because the
    // record's `update` REPLACES it (themeMode.js / navbarTabMenu.js carry
    // the same contract); the Reset row below leaves it alone (it resets
    // this device's four).
    SingleKeys: {
      extends: 'MemberProfileA11yRow',
      scope: {
        key: 'shortcutsSingleKeys',
        labelKey: 'account.member.a11y.singleKeys',
        hintKey: 'account.member.a11y.singleKeysHint',
        singleKeysOn(root) {
          const prefs = root && root.userPrefs
          return !(prefs && typeof prefs === 'object' && prefs.shortcutsSingleKeys === false)
        },
        async saveSingleKeys(el, root, on) {
          if (!root || typeof root.update !== 'function') return
          const prev = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : {}
          const next = { ...prev, shortcutsSingleKeys: !!on }
          root.update({ userPrefs: next }, { preventFetch: true })
          const sdk = el && el.sdk
          if (!sdk || typeof sdk.execute !== 'function') return
          try {
            await sdk.execute('workspaceProject.userPreferences', 'update', { payload: next })
          } catch (err) {
            // Roll the switch back: the record did not take it.
            root.update({ userPrefs: prev }, { preventFetch: true })
            console.warn('[MemberProfile] shortcutsSingleKeys save failed', err)
          }
        }
      },
      Control: {
        SwitchInput: {
          onCheck: (el, s) => el.parent.parent.scope.singleKeysOn(s.root),
          onClick: (e, el, s) => {
            const row = el.parent.parent
            return row.scope.saveSingleKeys(el, s.root, !row.scope.singleKeysOn(s.root))
          }
        }
      }
    }
  },

  // Stays mounted and only hides (Rule 66), so the row cannot flicker in and
  // out of the card's flow as the last preference returns to its default.
  ResetRow: {
    flow: 'x',
    align: 'center left',
    paddingTop: 'B',
    hide: (el, s) => el.call('a11yPrefsAreDefault', s.root),
    ResetBtn: {
      tag: 'button',
      type: 'button',
      padding: 'Y A',
      minHeight: 'controlHCompact',
      '@coarse': { minHeight: 'controlH' },
      round: 'radiusControl',
      borderWidth: '0',
      background: 'overlayStrong',
      color: 'title',
      fontSize: 'Z1',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'A defaultBezier filter',
      ':hover': { filter: 'brightness(0.96)' },
      text: (el) => el.call('polyglot', 'account.member.a11y.reset'),
      onClick: (e, el, s) => el.call('resetA11yPrefs', s.root)
    }
  }
})

// ⚠ TOP-LEVEL EXPORTS, not `MemberProfile.X = …`. Assigning to the component
// makes the object a CHILD of MemberProfile (that is exactly how every card
// above is declared), so it would both render as a stray card AND stay
// invisible to `extends: 'Name'`, which resolves against the component
// registry. Measured live 2026-09-04: the three switch rows still painted
// because each declares its own `Control: { SwitchInput: {} }` inline, while
// the type-scale row — whose chips come only from `childExtends` — rendered
// zero buttons.
//
// One preference row of the Accessibility card — the label/hint pair at the
// left, the control at the right, apart from the next by its own padding (no
// hairline inside a rounded card). Declared ONCE (Rules 61 +
// 65); instances carry the state key and their own strings. Same row geometry
// as the Notifications rows, so the preference cards read as one family.
//
// `SwitchInput` (the house iOS-style switch) reads and writes through the row's
// `scope.key`: the switch contract is an `onCheck` FUNCTION prop, because
// v3.14 evaluates ordinary function props once at create and freezes the
// result — see SwitchInput.js's own note.
//
// A row carries its strings as KEYS in `scope` (`labelKey`, `hintKey`), and the
// recipe resolves them. The switch takes the SAME label key as its accessible
// name: the three switches were `<button role="switch">` with no name at all
// (measured live on /my, 2026-09-20), so a screen reader announced "switch,
// off" three times on the very card that exists for it. The visible label is a
// sibling <span>, which names nothing. One key feeds both, so they cannot
// drift. `attr`, not a flat quoted prop — see SwitchInput.js on why a dash-key
// prop does not survive `extends`.
export const MemberProfileA11yRow = {
  scope: { key: '', labelKey: '', hintKey: '' },
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'B 0',

  RowLabel: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    LabelText: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el) => (el.scope.labelKey ? el.call('polyglot', el.scope.labelKey) : '')
    },
    Hint: {
      tag: 'span',
      fontSize: 'Z2',
      lineHeight: '1.4',
      color: 'caption',
      text: (el) => (el.scope.hintKey ? el.call('polyglot', el.scope.hintKey) : '')
    }
  },

  Control: {
    flow: 'x',
    align: 'center right',
    flexShrink: '0',
    SwitchInput: {
      attr: {
        'aria-label': (el) => (el.scope.labelKey ? el.call('polyglot', el.scope.labelKey) : null)
      },
      onCheck: (el, s) => !!s.root[el.parent.parent.scope.key],
      onClick: (e, el, s) => {
        const key = el.parent.parent.scope.key
        el.call('setA11yPref', s.root, key, !s.root[key])
      }
    }
  }
}

// One step of the type-scale control. An ACTION with a pressed state, not a
// switch: three chips, exactly one active.
export const MemberProfileA11yChip = {
  tag: 'button',
  type: 'button',
  scope: { pref: 'md', labelKey: 'account.member.a11y.fontSize' },
  attr: {
    'aria-pressed': (el, s) => (s.root.a11yFontScale === el.scope.pref ? 'true' : 'false')
  },
  aria: { label: (el) => el.call('polyglot', el.scope.labelKey) },
  boxSize: 'C C',
  '@coarse': { minWidth: 'controlH', minHeight: 'controlH' },
  flow: 'x',
  align: 'center center',
  round: 'radiusControl',
  borderWidth: '0',
  cursor: 'pointer',
  fontWeight: '700',
  lineHeight: '1',
  // The house selected pair: the ink capsule when active, the control surface
  // at rest — the same ramp the marketplace filter chips use.
  background: (el, s) => (s.root.a11yFontScale === el.scope.pref ? 'highlight' : 'overlayStrong'),
  color: (el, s) => (s.root.a11yFontScale === el.scope.pref ? 'highlight-reversed' : 'title'),
  transition: 'A defaultBezier filter',
  ':hover': { filter: 'brightness(0.96)' },
  onClick: (e, el, s) => el.call('setA11yPref', s.root, 'a11yFontScale', el.scope.pref)
}

// ── Assistant (self only) — the default ANSWER MODE ──
// ASSISTANT_BOARD_MODE_SPEC §1 (WS-W2-ASSISTANT-PAGE-MODE-DOCK-1): where the
// assistant's answers go — Page (a generated board on the home), Sidebar,
// Minimized. The SAME choice the assistant's settings panel carries (one
// writer, the shell's `setAssistantAnswersMode`: persisted per user, the last
// mode remembered), here where a person looks for their defaults. Hidden
// wherever the shell's writer is absent (another app mounting this page).
// Page is a PLACEMENT (the main thread, the default — Nika's pick 6 A): it
// needs no board service, so the card shows whether or not the generated-
// board service answers. The row wraps: the choice drops
// under its label on a narrow card rather than pushing out of it.
MemberProfile.SettingsMain.ProfileAssistant = {
  ..._card({
    attr: { 'data-content-section': 'Assistant' },
    gap: 'B',
    AssistantTitle: {
      tag: 'h2',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: (el) => el.call('polyglot', 'account.member.assistant.title')
    },
    AnswersRow: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center space-between',
      gap: 'A',
      padding: 'B 0',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        flex: '1 1 auto',
        minWidth: '0',
        LabelText: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: (el) => el.call('polyglot', 'account.member.assistant.answers')
        },
        Hint: {
          tag: 'span',
          fontSize: 'Z2',
          lineHeight: '1.4',
          color: 'caption',
          overflowWrap: 'anywhere',
          text: (el) => el.call('polyglot', 'account.member.assistant.answersHint')
        }
      },
      AppSegmented: {
        variant: 'choice',
        compact: true,
        ariaLabel: (el) => el.call('polyglot', 'account.member.assistant.answers'),
        current: (el, s) => {
          const dock = s.root && s.root.assistantDock
          return dock === 'main' ? 'page' : dock === 'minimized' ? 'minimized' : 'sidebar'
        },
        select: 'setAssistantAnswersMode',
        childrenAs: 'state',
        children: () => [
          { key: 'page', text: 'ai.answers.page', value: 'page' },
          { key: 'sidebar', text: 'ai.answers.sidebar', value: 'sidebar' },
          { key: 'minimized', text: 'ai.answers.minimized', value: 'minimized' }
        ]
      }
    }
  }),
  subscribeTo: ['assistantDock'],
  hide: (el) =>
    !el.call('isSelf') ||
    typeof (el.context && el.context.functions && el.context.functions.setAssistantAnswersMode) !==
      'function'
}

// ── Security (self only) ──
MemberProfile.SettingsSide.ProfileSecurity = _selfCard({
  attr: { 'data-content-section': 'Security' },
  gap: 'B',
  SecurityTitle: {
    tag: 'h2',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    text: '{{ account.member.security.title | polyglot }}'
  },
  SecurityRows: {
    flow: 'y',

    PwdRow: {
      // The label and its hint take their own line and the field sits under
      // them (stage 3): beside the field the hint wrapped to three lines in
      // the side stack on a 1920 px window.
      flow: 'y',
      gap: 'Z',
      padding: 'B 0',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        minWidth: '0',
        LabelText: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: '{{ account.member.security.password | polyglot }}'
        },
        Hint: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: '{{ account.member.security.passwordHint | polyglot }}'
        }
      },
      // The field takes the line and the FIELD gives way on a phone: field
      // and button kept 301 px in a 262 px card (live 390 px frame,
      // 2026-10-29; memberProfileNarrowRows.test.js). The button keeps its
      // width. Where the field would be shorter than F beside it, the button
      // wraps under the field: the field kept 99 px at 390 px and 128 px in
      // the 1440 px side stack (live 2026-10-29).
      PwdAction: {
        flow: 'x',
        flexWrap: 'wrap',
        gap: 'Z',
        align: 'center',
        minWidth: '0',
        PwdInput: {
          tag: 'input',
          type: 'password',
          placeholder: (el) => el.call('polyglot', 'account.member.placeholder.newPassword'),
          fontSize: 'Z2',
          theme: 'field',
          minHeight: 'controlH',
          padding: '0 A',
          round: 'radiusControl',
          color: 'title',
          flex: '1 1 var(--spacing-F)',
          minWidth: '0'
        },
        // The kit's ink capsule (Rule 68: the kit owns its height, inset and
        // states — it was a hand-made capsule with a hover and no pressed
        // step). The label stack keeps the width of the longest of its three
        // words, so "Updated" never resizes it (Rule 66).
        PwdBtn: {
          extends: 'AppCapsuleBtnPrimary',
          flexShrink: '0',
          AppLabelStack: {
            labels: [
              'account.member.security.update',
              'account.member.security.updated',
              'account.member.security.updateFailed'
            ],
            shown: (el, s) =>
              s._pwdStatus === 'account.member.security.updated'
                ? 1
                : s._pwdStatus === 'account.member.security.updateFailed'
                  ? 2
                  : 0
          },
          onClick: async (e, el, s) => {
            const input = el.parent.PwdInput?.node
            const pw = input?.value
            if (!pw || pw.length < 6) return
            try {
              // TODO[password-update]: SDK updateMe currently routes profile
              // edits — confirm server-side handles password rotation via this
              // path; if a dedicated flow is required, replace with that call.
              await el.sdk.updateMe({ password: pw })
              input.value = ''
              el.state.update({ _pwdStatus: 'account.member.security.updated' })
              setTimeout(() => el.state.update({ _pwdStatus: null }), 2000)
            } catch (err) {
              el.state.update({ _pwdStatus: 'account.member.security.updateFailed' })
              setTimeout(() => el.state.update({ _pwdStatus: null }), 2000)
            }
          }
        }
      }
    },

    TwoFa: _row(
      '{{ account.member.security.twoFa | polyglot }}',
      '{{ common.state.coming_soon | polyglot }}',
      {
        hint: '{{ account.member.security.twoFaHint | polyglot }}'
      }
    ),
    BackupCodes: _row(
      '{{ account.member.security.backupCodes | polyglot }}',
      '{{ common.state.coming_soon | polyglot }}',
      {}
    )
  }
})

// ── Notifications (self only) ──
MemberProfile.SettingsMain.ProfileNotifications = _selfCard({
  attr: { 'data-content-section': 'Notifications' },
  gap: 'B',
  NotifTitle: {
    tag: 'h2',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    text: '{{ account.member.notifications.title | polyglot }}'
  },
  NotifRows: {
    flow: 'y',

    EmailDigest: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        LabelText: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: '{{ account.member.notifications.digest | polyglot }}'
        },
        Hint: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: '{{ account.member.notifications.digestHint | polyglot }}'
        }
      },
      // The stored preference is a lowercase CODE ('off' | 'daily' |
      // 'weekly'); the label is derived from it, never stored.
      //
      // This row used to declare `childExtends: { tag: 'option', text }` with
      // `selected: v === cur` on each item. `childrenAs: 'state'` makes each
      // item the child's STATE, and the inline childExtends declared only
      // `tag` and `text` — so neither `value` nor `selected` ever reached the
      // DOM, and BOTH halves of the control were wrong: the select always
      // showed the first option, and `e.target.value` returned the option's
      // TEXT ('Daily'), which `_savePrefs` persisted as the preference.
      //
      // `value:` (controlled) is the form that survives, not `<option
      // selected>`: a native <select> ignores declarative selectedness when
      // its options arrive through `childrenAs`, and smbls re-asserts a
      // controlled `value` after every option (re)mount
      // (element/src/create.js syncSelectValue). `selected:` on the items is
      // gone — with a controlled value syncSelectValue never consults
      // defaultSelected at all, so it was dead weight that read as working.
      DigestSelect: {
        tag: 'select',
        theme: 'field',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusControl',
        color: 'title',
        fontSize: 'Z2',
        cursor: 'pointer',
        // The visible RowLabel is not associated with this control by
        // `for`/`id`, so a screen reader would otherwise announce an unnamed
        // combo box. Same fix as InterfaceLanguageSelect two cards up.
        'aria-label': (el) => el.call('polyglot', 'account.member.notifications.digestAria'),

        // FA204 — the code list travels ON the element, so no serialized
        // handler reads a bare module-scope binding (this file has been bitten
        // by exactly that: see the `scope-read-name-collision` note on the
        // profile Options select above).
        //
        // `current` is the LEGACY-TOLERANT read. Rows written before this fix
        // hold the label ('Off' / 'Daily' / 'Weekly'); lowercasing maps each
        // one back to its own code, because the labels were generated FROM
        // the codes. A value that is not a known code at all falls back to
        // the default rather than leaving the control blank — `node.value =
        // <no matching option>` sets selectedIndex to -1 and shows nothing.
        scope: {
          CODES: ['off', 'daily', 'weekly'],
          DEFAULT: 'daily',
          current(stored) {
            const code = String(stored == null ? '' : stored).toLowerCase()
            return this.CODES.indexOf(code) === -1 ? this.DEFAULT : code
          }
        },

        // FA808 / Rule 10 — a NAMED component, never an inline
        // `childExtends: {…}` object: frank's serializer cannot carry the
        // inline form, so the published build renders an option-less select.
        // LanguageOption (components/LanguageSelectField.js) is the platform's
        // `{ value, text }` <option> and holds no language-specific logic, so
        // Rule 63 says reuse it rather than mint a fourth near-identical
        // option component beside AppOption and IntegrationScopeOption. Its
        // NAME is the only thing domain-specific about it; that should
        // collapse to a neutral one when LanguageSelectField.js is next open.
        childExtends: 'LanguageOption',
        childrenAs: 'state',
        children: (el) =>
          el.scope.CODES.map((v) => ({
            value: v,
            text: v.charAt(0).toUpperCase() + v.slice(1)
          })),

        // FA515 — `s.root.userPrefs` is read UNCONDITIONALLY so the signal is
        // tracked. `userPrefs` boots as null and lands later from
        // loadPrivateData; a `s.root?.userPrefs` guard short-circuits on that
        // first run, never subscribes, and the control then freezes on the
        // default forever.
        value: (el, s) => el.scope.current(s.root.userPrefs?.email_digest),
        onChange: (e, el, s) => _savePrefs(s.root, { email_digest: e.target.value })
      }
    },

    Mentions: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      RowLabel: {
        flow: 'y',
        gap: 'Y',
        LabelText: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          text: '{{ account.member.notifications.mentions | polyglot }}'
        },
        Hint: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: '{{ account.member.notifications.mentionsHint | polyglot }}'
        }
      },
      // Identical shape and identical defect to DigestSelect above — see the
      // full note there. Codes: 'off' | 'realtime' | 'daily'.
      //
      // The label expression here used to special-case `v === 'realtime' ?
      // 'Realtime' : capitalise(v)`. That ternary was a no-op — capitalising
      // 'realtime' already yields 'Realtime' — so it is gone; one rule now
      // produces every label, which is also what makes `toLowerCase()` an
      // exact inverse for the legacy rows.
      MentionSelect: {
        tag: 'select',
        theme: 'field',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusControl',
        color: 'title',
        fontSize: 'Z2',
        cursor: 'pointer',
        'aria-label': (el) => el.call('polyglot', 'account.member.notifications.mentionsAria'),

        scope: {
          CODES: ['off', 'realtime', 'daily'],
          DEFAULT: 'realtime',
          current(stored) {
            const code = String(stored == null ? '' : stored).toLowerCase()
            return this.CODES.indexOf(code) === -1 ? this.DEFAULT : code
          }
        },

        childExtends: 'LanguageOption',
        childrenAs: 'state',
        children: (el) =>
          el.scope.CODES.map((v) => ({
            value: v,
            text: v.charAt(0).toUpperCase() + v.slice(1)
          })),

        value: (el, s) => el.scope.current(s.root.userPrefs?.mention_notifications),
        onChange: (e, el, s) => _savePrefs(s.root, { mention_notifications: e.target.value })
      }
    },

    Birthdays: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      RowLabel: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.notifications.birthdays | polyglot }}'
      },
      BirthdayToggle: {
        tag: 'button',
        minHeight: 'controlHCompact',
        padding: '0 Z',
        round: 'radiusPill',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '700',
        // §4.3 selection wash + title ink — the old white-on-green state chip
        // measured 2.81:1 (needs 4.5; AA walk 2026-08-08). Hue never marks
        // selection; the On/Off label carries the state.
        background: (el, s) =>
          s.root?.userPrefs?.birthday_reminders !== false ? 'selected' : 'overlay',
        color: (el, s) => (s.root?.userPrefs?.birthday_reminders !== false ? 'title' : 'caption'),
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.userPrefs?.birthday_reminders !== false
              ? 'account.member.toggle.on'
              : 'account.member.toggle.off'
          ),
        onClick: (e, el, s) => {
          const cur = s.root?.userPrefs?.birthday_reminders !== false
          _savePrefs(s.root, { birthday_reminders: !cur })
        }
      }
    },

    // Gates the chime openNotification's `sound: true` toasts play
    // (shared/functions/notifySound.js reads this same key). Default ON,
    // same idiom as Birthdays above — the terminal row now, so no bottom
    // border.
    NotifSound: {
      flow: 'x',
      align: 'center space-between',
      padding: 'B 0',
      RowLabel: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.notifications.sound | polyglot }}'
      },
      NotifSoundToggle: {
        tag: 'button',
        minHeight: 'controlHCompact',
        padding: '0 Z',
        round: 'radiusPill',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '700',
        // §4.3 selection wash + title ink (same AA fix as BirthdayToggle).
        background: (el, s) =>
          s.root?.userPrefs?.notification_sound !== false ? 'selected' : 'overlay',
        color: (el, s) => (s.root?.userPrefs?.notification_sound !== false ? 'title' : 'caption'),
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.userPrefs?.notification_sound !== false
              ? 'account.member.toggle.on'
              : 'account.member.toggle.off'
          ),
        onClick: (e, el, s) => {
          const cur = s.root?.userPrefs?.notification_sound !== false
          _savePrefs(s.root, { notification_sound: !cur })
        }
      }
    }
  }
})

// ── Sessions (self only) ──
MemberProfile.SettingsSide.ProfileSessions = _selfCard({
  attr: { 'data-content-section': 'Sessions' },
  gap: 'A',
  SessTitle: {
    tag: 'h2',
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    text: '{{ account.member.session.title | polyglot }}'
  },
  SessionInfo: {
    flow: 'y',
    gap: 'Z',
    padding: 'Z 0',
    Browser: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z 0',
      RowLabel: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.session.browser | polyglot }}'
      },
      RowValue: {
        tag: 'span',
        fontSize: 'A',
        color: 'paragraph',
        text: (el) => {
          if (typeof navigator === 'undefined') return '—'
          const ua = navigator.userAgent
          if (ua.includes('Chrome')) return 'Chrome'
          if (ua.includes('Firefox')) return 'Firefox'
          if (ua.includes('Safari')) return 'Safari'
          return 'Unknown'
        }
      }
    },
    Platform: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z 0',
      RowLabel: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.session.platform | polyglot }}'
      },
      RowValue: {
        tag: 'span',
        fontSize: 'A',
        color: 'paragraph',
        text: (el) => {
          if (typeof navigator === 'undefined') return '—'
          const p = navigator.platform || ''
          if (p.includes('Mac')) return 'macOS'
          if (p.includes('Win')) return 'Windows'
          if (p.includes('Linux')) return 'Linux'
          return p || 'Unknown'
        }
      }
    },
    SignedIn: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z 0',
      RowLabel: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.session.status | polyglot }}'
      },
      // Status hue as a DOT + neutral ink (wave-1 RoleDot grammar) — green as
      // small-text ink measured 2.48:1 on the light stage (needs 4.5).
      RowValue: {
        tag: 'span',
        flow: 'x',
        align: 'center',
        gap: 'Y',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        StatusDot: {
          tag: 'span',
          width: 'Y',
          height: 'Y',
          round: '50%',
          flex: '0 0 auto',
          background: 'green'
        },
        StatusText: { tag: 'span', text: '{{ account.member.session.activeNow | polyglot }}' }
      }
    }
  }
})

// ── Danger zone (self only) ──
MemberProfile.SettingsSide.ProfileDanger = {
  ..._card({}),
  attr: { 'data-content-section': 'Danger zone' },
  hide: (el) => !el.call('isSelf'),
  // ONE BUTTON COLUMN (current-2a, 2026-09-26: "one shared width for the
  // three buttons = the widest label of the three in the active language,
  // armed labels included, so the right edge stays even; keep 44px"). The
  // card is a two-track grid — the labels, then an `auto` track the three
  // buttons share. Each button's own width is its widest label (the label
  // stack below), the `auto` track is the widest of the three, and every
  // button stretches to it. The rows are `display: contents`, so their label
  // and button land in the card's tracks while each row keeps its own state.
  // (`_card` makes the card a flex column; the grid replaces that.)
  flow: null,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'center',
  columnGap: 'A',
  rowGap: 'B',
  // A phone has no room for two tracks: one column, the buttons full width
  // (equal by construction), each under its own label.
  '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 'Z' },
  // KEPT — the ONE card in this file with a visible edge: a semantic danger
  // accent (not the row/card separation habit `_card` used to carry). §4.2
  // danger role via `redInk` (adaptive [#B93A24, red]) — brand red as ink
  // measured 2.82:1 on the light stage (title needs 3.0, the buttons 4.5;
  // AA walk 2026-08-08). redInk holds ≥5.0:1 light / ≥5.7:1 dark.
  borderWidth: 'hairline',
  borderStyle: 'solid',
  borderColor: 'redInk',
  DangerTitle: {
    tag: 'h2',
    gridColumn: '1 / -1',
    fontSize: 'B',
    fontWeight: '700',
    color: 'redInk',
    text: '{{ account.member.danger.title | polyglot }}'
  },
  DangerHint: {
    tag: 'p',
    gridColumn: '1 / -1',
    fontSize: 'Z2',
    color: 'caption',
    text: '{{ account.member.danger.hint | polyglot }}'
  },

  // THE ROWS. Each ARMS on the first click and acts on the second, and each
  // OWNS its armed flag: with no `state` of their own the rows shared one
  // `_confirming` on an ancestor, so arming Sign out armed Deactivate too (the
  // defect /admin/danger fixed on 2026-09-24). The second half of a
  // double-click is NOT a confirm (a click with `detail > 1` is dropped):
  // arming and acting are two gestures. Keyboard activation (`detail` 0)
  // still works. The control is the kit's destructive capsule (the
  // `dangerInk` pair on a wash of that ink); armed deepens the wash through a
  // flat `dataArmed` hook and a static selector — not a reactive `background`
  // (that paints inline, over :hover / :active) and not a solid red slab
  // (DESIGN_LANGUAGE §4.2: danger is ink on a tint).
  //
  // THE LABEL STACK (Rule 66): every label a button can show sits in ONE grid
  // cell — the kit's `AppLabelStack` (D2 e0c4487f7, which replaced the three
  // stacks this card built by hand) — and only the `shown` one is visible,
  // so each capsule is as wide as its LONGEST label in the active language
  // from the first paint and arming never resizes it. A fixed `minWidth`
  // could not do this: "Confirm deactivation?" measures 202 px in English and
  // 294 px in Georgian (TBC Contractica Medium at Z2, + the capsule's inset).
  // A label under `visibility: hidden` is out of the accessible name. The
  // stack sizes each button; the card's shared `auto` track (above) makes
  // the three one width.
  SignOutRow: {
    state: { _confirming: false },
    // The row's own box goes (the card's grid places its label and button);
    // its state stays. No row hover wash: a row is not a control.
    display: 'contents',
    RowLabel: {
      flow: 'y',
      gap: 'Y',
      LabelText: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.danger.signOut | polyglot }}'
      },
      Hint: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: '{{ account.member.danger.signOutHint | polyglot }}'
      }
    },
    SignOutBtn: {
      extends: 'AppCapsuleBtnDanger',
      // Fills the shared button track (the widest label of the three).
      justifySelf: 'stretch',
      flexShrink: '0',
      dataArmed: (el, s) => !!s._confirming,
      '&[data-armed]': {
        background: 'color-mix(in srgb, currentColor 20%, var(--color-tintBase))'
      },
      AppLabelStack: {
        labels: ['account.member.danger.signOutBtn', 'account.member.danger.signOutConfirm'],
        shown: (el, s) => (s._confirming ? 1 : 0)
      },
      onClick: async (e, el, s) => {
        if (e.detail > 1) return
        if (!s._confirming) {
          el.state.update({ _confirming: true })
          setTimeout(() => el.state.update({ _confirming: false }), 3000)
          return
        }
        await signoutAndRedirect({ sdk: el.sdk })
      }
    }
  },

  DeactivateRow: {
    state: { _confirming: false },
    display: 'contents',
    RowLabel: {
      flow: 'y',
      gap: 'Y',
      LabelText: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ account.member.danger.deactivate | polyglot }}'
      },
      Hint: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: '{{ account.member.danger.deactivateHint | polyglot }}'
      }
    },
    DeactivateBtn: {
      extends: 'AppCapsuleBtnDanger',
      // Fills the shared button track (the widest label of the three).
      justifySelf: 'stretch',
      flexShrink: '0',
      dataArmed: (el, s) => !!s._confirming,
      '&[data-armed]': {
        background: 'color-mix(in srgb, currentColor 20%, var(--color-tintBase))'
      },
      AppLabelStack: {
        labels: ['account.member.danger.deactivateBtn', 'account.member.danger.deactivateConfirm'],
        shown: (el, s) => (s._confirming ? 1 : 0)
      },
      onClick: async (e, el, s) => {
        if (e.detail > 1) return
        if (!s._confirming) {
          el.state.update({ _confirming: true })
          setTimeout(() => el.state.update({ _confirming: false }), 3000)
          return
        }
        try {
          await el.sdk.execute('workspaceProject.userProfiles', 'update', {
            id: s.root.currentUserId,
            payload: { status: 'left' }
          })
          await signoutAndRedirect({ sdk: el.sdk })
        } catch (err) {
          console.warn('deactivate failed', err)
        }
      }
    }
  },

  // DELETE ORGANIZATION — moved here from the workspace switcher (Q10 A, Nika
  // 2026-09-26: a destructive org action does not belong in the menu people
  // open to switch). Same gate as the switcher row it replaces: the OWNER of
  // the active org (the server's DELETE /organizations/:id is owner-only
  // too). Same confirm step: arm, then act; the arm lapses after 4 s.
  // Never a PERSONAL org: it goes with its account (MODEL.md invariant 1),
  // and the server answers 400 `personal_org_undeletable` (2026-10-28). The
  // row checked the role only before, so the owner of a personal org was
  // offered the delete.
  DeleteOrgRow: {
    state: { _confirming: false, _busy: false },
    scope: {
      // The org this row deletes: the SETTLED active org — `root.org`, the
      // /auth/me OrgMember row — matched in the membership list for its
      // role. Stricter than the switcher's display resolver ON PURPOSE: no
      // `orgs[0]` fallback, and nothing while a switch is in flight
      // (`_pendingActiveOrgId`) — a destructive action never guesses its
      // target.
      //
      // A PERSONAL org is no target. The type is read the way the shell
      // reads it elsewhere (inviteRoles.js, workspaceRename.js): the
      // membership row, and the active org row with the same id. Either one
      // saying `personal` is enough, and the name never decides.
      targetOrg(root) {
        if (!root || root._pendingActiveOrgId) return null
        const org = root.org
        const id = org && (org.id || org._id)
        if (!id) return null
        const list = Array.isArray(root.orgs) ? root.orgs : []
        const row = list.find((o) => o && o.id === id) || null
        if (!row || row.type === 'personal' || org.type === 'personal') return null
        return row
      },
      // What a refused delete says. The SDK throws the server's English
      // message with the JSON body on `cause` (BaseService._request), or
      // resolves `{ ok: false, error }` for a refusal it reads off a 2xx
      // body. A personal org reads in plain words, in the page language.
      failureText(el, err) {
        const body = (err && err.cause) || err || {}
        if (body.error === 'personal_org_undeletable') {
          return el.call('polyglot', 'account.member.danger.personalOrgUndeletable')
        }
        return (err && (err.message || err.error)) || String(err)
      }
    },
    hide: (el, s) => {
      const org = el.scope.targetOrg(s.root)
      return !(org && org.role === 'owner')
    },
    display: 'contents',
    RowLabel: {
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      LabelText: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: (el) => el.call('polyglot', 'shell.orgSwitcher.deleteOrg')
      },
      // The TARGET, by name: the row never offers to delete "this
      // organization" without saying which one.
      OrgName: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => {
          const org = el.scope.targetOrg(s.root)
          return (org && org.name) || ''
        }
      }
    },
    DeleteOrgBtn: {
      extends: 'AppCapsuleBtnDanger',
      // Fills the shared button track (the widest label of the three).
      justifySelf: 'stretch',
      // Its own store for the disarm timer; `targetOrg` still resolves up the
      // scope chain from the row.
      scope: { disarm: null },
      flexShrink: '0',
      dataArmed: (el, s) => !!s._confirming,
      '&[data-armed]': {
        background: 'color-mix(in srgb, currentColor 20%, var(--color-tintBase))'
      },
      disabled: (el, s) => !!s._busy,
      AppLabelStack: {
        labels: [
          'common.action.delete',
          'shell.orgSwitcher.deleteConfirm',
          'shell.orgSwitcher.deleting'
        ],
        shown: (el, s) => (s._busy ? 2 : s._confirming ? 1 : 0)
      },
      onClick: async (e, el, s) => {
        if (e.detail > 1) return
        if (s._busy) return
        if (!s._confirming) {
          el.state.update({ _confirming: true })
          clearTimeout(el.scope.disarm)
          el.scope.disarm = setTimeout(() => el.state.update({ _confirming: false }), 4000)
          return
        }
        clearTimeout(el.scope.disarm)
        const org = el.scope.targetOrg(s.root)
        if (!org || org.role !== 'owner') {
          el.state.update({ _confirming: false })
          return
        }
        el.state.update({ _confirming: false, _busy: true })
        const fail = (err) => {
          console.warn(
            '[MemberProfile] deleteOrganization failed',
            err?.message || err?.error || err
          )
          el.state.update({ _busy: false })
          el.call('openNotification', {
            title: el.call('polyglot', 'shell.orgSwitcher.deleteFailed'),
            message: el.scope.failureText(el, err),
            type: 'error'
          })
        }
        let res
        try {
          res = await el.sdk.deleteOrganization(org.id)
        } catch (err) {
          fail(err)
          return
        }
        // A refusal the SDK RESOLVES keeps the org: before this check it
        // left the membership list as if it were deleted.
        if (res && res.ok === false) {
          fail(res)
          return
        }
        // As the switcher row did (#2276): the org leaves the membership
        // list, and the auth tick re-resolves the active org. This row then
        // hides with it.
        const nextOrgs = (s.root.orgs || []).filter((o) => o.id !== org.id)
        s.root.update({ orgs: nextOrgs, _pendingActiveOrgId: null })
        el.state.update({ _busy: false })
      },
      onRemove: (el) => clearTimeout(el.scope && el.scope.disarm)
    }
  }
}

// ── Member access (superadmin, viewing OTHER members) ──
// Per-member read-only toggle. status 'inactive' = read-only workspace access:
// the member can view everything but the auth middleware 403s their writes, and
// they're filtered out of active rosters/calendars/channels. Sets the canonical
// Mongo User.status via sdk.setMemberStatus(orgId, …) → the org-admin-gated
// PATCH /organizations/:orgId/members/:userId/status route. Gated on
// canSuperadmin (mirrors the server's org-management role gate) and !isSelf.
MemberProfile.ProfileSide.ProfileAccess = {
  ..._card({}),
  attr: { 'data-content-section': 'Member access' },
  hide: (el, s) => el.call('isSelf', s) || !el.call('canSuperadmin', s),
  AccessTitle: {
    extends: 'AppMicroLabel',
    text: (el) => el.call('polyglot', 'account.member.access.title')
  },
  AccessHint: {
    tag: 'p',
    fontSize: 'Z2',
    color: 'caption',
    text: (el) => el.call('polyglot', 'account.member.access.hint')
  },
  // State on the left, the one action on the right; the action drops under
  // the state on a narrow card. The state reads in title ink — the hue
  // (`blue` ink, `blue.1` / `green.1` plates) carried no status meaning and
  // the plates failed the brand's no-raw-hue rule (live 2026-10-28).
  AccessRow: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Z A',
    RowLabel: {
      flow: 'y',
      gap: 'Y',
      flex: '1 1 var(--spacing-F)',
      minWidth: '0',
      LabelText: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '500',
        color: 'title',
        text: (el, s) => {
          const st = findMe(s)?.status
          return el.call(
            'polyglot',
            st === 'inactive'
              ? 'account.member.access.inactive'
              : st === 'left'
                ? 'account.member.access.left'
                : 'account.member.access.active'
          )
        }
      },
      Hint: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) =>
          el.call(
            'polyglot',
            findMe(s)?.status === 'inactive'
              ? 'account.member.access.readOnlyHint'
              : 'account.member.access.fullHint'
          )
      }
    },
    AccessBtn: {
      extends: 'AppCapsuleBtnToolbar',
      // Busy = the real disabled attribute; the stack keeps the capsule as
      // wide as its longest label in the active language (Rule 66).
      disabled: (el, s) => !!s._accessBusy,
      'aria-busy': (el, s) => !!s._accessBusy,
      AppLabelStack: {
        labels: [
          'account.member.access.setReadOnly',
          'account.member.access.restore',
          'account.member.access.saving'
        ],
        shown: (el, s) => (s._accessBusy ? 2 : findMe(s)?.status === 'inactive' ? 1 : 0)
      },
      onClick: async (e, el, s) => {
        const person = findMe(s)
        if (!person?.userId || s._accessBusy) return
        const orgId = s.root?.org?.id || el.sdk?._context?.activeOrgId
        if (!orgId) {
          console.warn('[MemberProfile] setMemberStatus: no active org id')
          return
        }
        const next = person.status === 'inactive' ? 'active' : 'inactive'
        el.state.update({ _accessBusy: true })
        try {
          await el.sdk.setMemberStatus(orgId, person.userId, next)
          s.root.update({
            people: patchPeopleList(s.root.people, person.userId, { status: next }),
            peopleAll: patchPeopleList(s.root.peopleAll, person.userId, { status: next })
          })
        } catch (err) {
          console.warn('[MemberProfile] setMemberStatus failed', err?.message || err)
        } finally {
          el.state.update({ _accessBusy: false })
        }
      }
    }
  }
}

// One signed document in the Documents card (registered, not an inline
// childExtends — Rule 10). The sprite `document` glyph replaces the emoji;
// the meta line is built from translated parts, never an English literal.
export const MemberProfileDocRow = {
  // A native button: Enter, Space and the focus ring come with the tag.
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center',
  gap: 'Z1',
  width: '100%',
  padding: 'Z1 Z',
  round: 'radiusControl',
  border: 'none',
  background: 'transparent',
  color: 'title',
  textAlign: 'left',
  cursor: 'pointer',
  disabled: (el, s) => !s.storagePath,
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  ':disabled': { cursor: 'default', background: 'transparent' },
  // migrated 2026-04-27 to SDK route workspaceProject.storage.signedUrl
  // (replaces sb().storage.from('contracts').createSignedUrl(path, 300)).
  onClick: async (e, el, s) => {
    if (!s.storagePath) return
    try {
      const result = await el.sdk.execute('workspaceProject.storage', 'signedUrl', {
        bucket: 'contracts',
        path: s.storagePath,
        ttl: 300
      })
      const url = result?.url || result?.signedUrl
      if (url) window.open(url, '_blank')
      else console.warn('[MemberProfile] signed URL response missing url field', result)
    } catch (err) {
      console.warn('[MemberProfile] signed url failed', err?.message || err)
    }
  },
  Icon: {
    name: 'document',
    boxSize: 'A',
    flexShrink: '0',
    color: 'caption',
    ariaHidden: 'true'
  },
  DocInfo: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    DocTitle: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '500',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => s.title
    },
    DocMeta: {
      tag: 'span',
      hide: (el, s) => s.size == null && !s.signedAt,
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) =>
        [
          s.size != null
            ? el.call('polyglot', 'account.member.documents.sizeKb').replace('{size}', s.size)
            : '',
          s.signedAt
            ? el.call('polyglot', 'account.member.documents.signedOn').replace('{date}', s.signedAt)
            : ''
        ]
          .filter(Boolean)
          .join(' · ')
    }
  },
  DocAction: {
    tag: 'span',
    hide: (el, s) => !s.storagePath,
    fontSize: 'Z2',
    fontWeight: '500',
    // §4.4 accent ink (AA on both stages; brand blue is 3.2:1 on light).
    color: 'accentInk',
    text: (el) => el.call('polyglot', 'account.member.documents.open')
  }
}

export const MemberProfileCheckItem = {
  flow: 'x',
  // The mark sits on the label's FIRST line: the body wraps on a phone.
  align: 'flex-start',
  gap: 'Z1',
  padding: 'Y 0',
  // The done mark is the house check glyph in ink (it was a green text ✓ and
  // a ○); a pending row keeps the glyph's box so every label starts on one
  // line (Rule 66).
  CheckIcon: {
    extends: 'Icon',
    name: 'check',
    boxSize: 'A',
    flexShrink: '0',
    // Centres the A box on the label's first line (a 1.5 line, a 1 em box).
    marginTop: 'X',
    color: 'title',
    visibility: (el, s) => (s.done ? 'visible' : 'hidden'),
    ariaHidden: 'true'
  },
  // The label and its status share a line while it holds both; on a phone
  // the status drops under the label, aligned with its text. Beside the
  // status the label kept its longest Georgian word and pushed the status
  // 51 px past the card edge, and with only `minWidth: 0` it wrapped to five
  // lines in 105 px (live 390 px frame, 2026-10-29;
  // memberProfileNarrowRows.test.js).
  CheckBody: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Y Z1',
    flex: '1',
    minWidth: '0',
    CheckLabel: {
      tag: 'span',
      fontSize: 'A',
      flex: '1 1 var(--spacing-F)',
      minWidth: '0',
      overflowWrap: 'anywhere',
      color: (el, s) => (s.done ? 'title' : 'caption'),
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    // Status hue as a DOT + neutral ink (wave-1 RoleDot grammar) — orange as
    // small-text ink measured 2.4:1 ×8 on the light stage (needs 4.5).
    CheckStatus: {
      tag: 'span',
      flow: 'x',
      align: 'center',
      gap: 'Y',
      flexShrink: '0',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      StatusDot: {
        tag: 'span',
        width: 'Y',
        height: 'Y',
        round: '50%',
        flex: '0 0 auto',
        background: (el, s) => (s.done ? 'green' : 'orange')
      },
      StatusText: {
        tag: 'span',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.done ? 'account.member.checklist.done' : 'account.member.checklist.pending'
          )
      }
    }
  }
}

// ProfileGrantCard and _grantInputBase moved to /financials/components/
// CapTableGrantModal.js — equity editing is no longer part of the active app.

// ── Stub export so any lingering bare-key `extends: 'ProfileGrantCard'` ──
// references in shared libraries don't cause a missing-component error at boot.
// Remove entirely once all shared consumers are updated.

// ProfileGrantCard body removed — moved to /financials/components/CapTableGrantModal.js
// Stub kept so bare-key extends resolution does not error at boot.
export const ProfileGrantCard = {}
